/**
 * VEDANGJYOTISHAM V6.x — SPIRITUALITY INTELLIGENCE MASTER ENGINE (SIME V1.0)
 * 
 * Foundational Jyotish Intelligence Engine for Spirituality & Spiritual Path.
 * Replaces static templates with a deterministic, evidence-based, chart-specific
 * classical Jyotish engine covering the 20 analytical dimensions.
 * 
 * Core Architectural Invariants:
 * 1. Single Common Jyotish Core (D1, Bhavas, Grahas, D9 Navamsha, D20 Vimsamsha, Dasha, Gochar, Jaimini AK/BK).
 * 2. High Variation Across Birth Charts: Every profile computes genuine distinct calculations for all 8 cards.
 * 3. Human Agency Preserved: Astrology illuminates innate tendencies; seeker maintains freedom of action.
 * 4. Temporal Fidelity: Current State anchored strictly to Focus Date; Horizon defines future discovery.
 * 5. Seeker (8 concise cards) & Acharya (Forensic deep dive) originate from the exact same Evidence Object.
 */

import { CompleteKundali, Language, PlanetName, ZodiacSign } from '../../types/kundali';
import {
  SpiritualInclinationClassification,
  SpiritualStabilityType,
  SpiritualOrientationType,
  SpiritualOrientationItem,
  SpiritualPathResult,
  SadhanaPracticeResult,
  SpiritualExperienceTheme,
  SpiritualTransformationTheme,
  SeekerPractitionerStage,
  SpiritualDevelopmentalStage,
  VairagyaThemeResult,
  MokshaOrientationResult,
  DharmaMokshaPatternType,
  DharmaMokshaBalanceResult,
  SpiritualityRelationshipResult,
  SpiritualityCareerResult,
  SpiritualityMaterialLifeResult,
  GuruTraditionResult,
  JnanaThemeResult,
  SpiritualLifeChapter,
  CurrentSpiritualStateResult,
  SpiritualActivationWindow,
  SpiritualChallenge,
  SpiritualIntegrationResult,
  SpiritualityEvidenceItem,
  SpiritualityContradictionItem,
  SpiritualityAlternativeItem,
  SpiritualityEvidenceObject,
  SeekerSpiritualityPresentation,
  AcharyaSpiritualityPresentation,
  MasterSpiritualityResult,
  SpiritualPanchangaContext
} from '../../types/spiritualityIntelligence';
import { determineAtmakarakaAndAmatyakaraka, getSignLord, getKarakamshaSign } from './lifePurposeEngine';
import { calculateAuthoritativePanchanga, PanchangaCoordinates } from '../panchangaEngine';

// ============================================================================
// ASTRONOMICAL & ZODIAC CONSTANTS & HELPERS
// ============================================================================
const ZODIAC_SIGNS: ZodiacSign[] = [
  'Aries', 'Taurus', 'Gemini', 'Cancer',
  'Leo', 'Virgo', 'Libra', 'Scorpio',
  'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

export function getZodiacSignIndex(sign: string): number {
  const idx = ZODIAC_SIGNS.indexOf(sign as ZodiacSign);
  return idx >= 0 ? idx : 0;
}

export function getHouseFromSign(fromSign: ZodiacSign, targetSign: ZodiacSign): number {
  const fromIdx = getZodiacSignIndex(fromSign);
  const targetIdx = getZodiacSignIndex(targetSign);
  return ((targetIdx - fromIdx + 12) % 12) + 1;
}

export function getSignsElement(sign: ZodiacSign): 'FIRE' | 'EARTH' | 'AIR' | 'WATER' {
  switch (sign) {
    case 'Aries':
    case 'Leo':
    case 'Sagittarius':
      return 'FIRE';
    case 'Taurus':
    case 'Virgo':
    case 'Capricorn':
      return 'EARTH';
    case 'Gemini':
    case 'Libra':
    case 'Aquarius':
      return 'AIR';
    case 'Cancer':
    case 'Scorpio':
    case 'Pisces':
    default:
      return 'WATER';
  }
}

// Calculate chronological age from birth details and focus date
export function calculateNativeAgeInYears(birthDateStr?: string, focusDate: Date = new Date()): number {
  if (!birthDateStr) return 35; // reasonable fallback
  const birth = new Date(birthDateStr);
  if (isNaN(birth.getTime())) return 35;
  let age = focusDate.getFullYear() - birth.getFullYear();
  const m = focusDate.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && focusDate.getDate() < birth.getDate())) {
    age--;
  }
  return Math.max(1, Math.min(115, age));
}

// Approximate Current Gochar (Transit) signs for 2024-2026 window
export function getReferenceTransitSigns(focusDate: Date): {
  jupiterSign: ZodiacSign;
  saturnSign: ZodiacSign;
  rahuSign: ZodiacSign;
  ketuSign: ZodiacSign;
} {
  const year = focusDate.getFullYear();
  const month = focusDate.getMonth() + 1;

  // Jupiter transits: Taurus (May 2024 - May 2025), Gemini (May 2025 - June 2026), Cancer (June 2026+)
  let jupiterSign: ZodiacSign = 'Taurus';
  if (year === 2025 && month >= 5) jupiterSign = 'Gemini';
  else if (year >= 2026 && month >= 6) jupiterSign = 'Cancer';
  else if (year >= 2026) jupiterSign = 'Gemini';

  // Saturn transits: Aquarius (until March 2025), Pisces (March 2025 - Feb 2028)
  let saturnSign: ZodiacSign = 'Aquarius';
  if (year > 2025 || (year === 2025 && month >= 3)) saturnSign = 'Pisces';

  // Rahu / Ketu transits: Rahu Pisces / Ketu Virgo (until May 2025), Rahu Aquarius / Ketu Leo (May 2025 - Nov 2026)
  let rahuSign: ZodiacSign = 'Pisces';
  let ketuSign: ZodiacSign = 'Virgo';
  if (year > 2025 || (year === 2025 && month >= 5)) {
    rahuSign = 'Aquarius';
    ketuSign = 'Leo';
  }

  return { jupiterSign, saturnSign, rahuSign, ketuSign };
}

// ============================================================================
// HELPER: CALCULATE BHRATRIKARAKA (BK - GURU & MENTOR SIGNIFICATOR IN JAIMINI)
// ============================================================================
export function determineBhratrikaraka(kundali: CompleteKundali): { bk: PlanetName; bkLong: number } {
  const planetLongs: { name: PlanetName; normLong: number }[] = [];
  const validPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu'];

  if (kundali.planets && Array.isArray(kundali.planets)) {
    for (const p of kundali.planets) {
      if (validPlanets.includes(p.name)) {
        const degInSign = (p.longitude || 0) % 30;
        planetLongs.push({ name: p.name, normLong: degInSign });
      }
    }
  }

  planetLongs.sort((a, b) => b.normLong - a.normLong);
  const bk = planetLongs[2]?.name || 'Jupiter';
  const bkLong = planetLongs[2]?.normLong || 18.4;
  return { bk, bkLong };
}

// ============================================================================
// HELPER: DETERMINE ISHTA DEVATA (JAIMINI UPADESHA SUTRAS 1.2.69-72)
// 12th from Karakamsha in D9 (Navamsha) reveals the personal spiritual deity
// ============================================================================
export interface IshtaDevataResult {
  planetSignifier: PlanetName;
  deityEn: string;
  deityHi: string;
  mantraEn: string;
  mantraHi: string;
  twelfthSignD9: ZodiacSign;
  explanationEn: string;
  explanationHi: string;
}

export function determineIshtaDevata(
  kundali: CompleteKundali,
  ak: PlanetName
): IshtaDevataResult {
  const karakamsha = getKarakamshaSign(kundali, ak);
  const karakamshaSign = karakamsha.signEn as ZodiacSign;
  const karakamshaIdx = getZodiacSignIndex(karakamshaSign);
  
  // 12th from Karakamsha in D9
  const twelfthIdx = (karakamshaIdx + 11) % 12;
  const twelfthSignD9 = ZODIAC_SIGNS[twelfthIdx];
  const twelfthLordD9 = getSignLord(twelfthSignD9);

  // Check occupants of 12th from Karakamsha in D9
  const d9Chart = kundali.charts?.D9;
  const d9TwelfthOccupants = (d9Chart?.planets || []).filter(p => p.sign === twelfthSignD9);

  // Preference: strong occupant, otherwise 12th lord in D9
  const primeSignifier: PlanetName = d9TwelfthOccupants.length > 0
    ? (d9TwelfthOccupants[0].name as PlanetName)
    : twelfthLordD9;

  switch (primeSignifier) {
    case 'Sun':
      return {
        planetSignifier: 'Sun',
        deityEn: 'Lord Shiva / Gayatri Devi / Surya Narayana',
        deityHi: 'भगवान शिव / गायत्री देवी / सूर्य नारायण',
        mantraEn: 'Om Namah Shivaya / Gayatri Mantra',
        mantraHi: 'ॐ नमः शिवाय / ॐ भूर्भुवः स्वः तत्सवितुर्वरेण्यम्',
        twelfthSignD9,
        explanationEn: `12th from Karakamsha (${twelfthSignD9}) governed by Sun signifies soul purification through light, truth, and Shiva Upasana.`,
        explanationHi: `कारकांश से १२वें भाव (${twelfthSignD9}) में सूर्य का प्रभाव आत्म-प्रकाश, सत्य एवं शिव साधना की प्रेरणा देता है।`
      };
    case 'Moon':
      return {
        planetSignifier: 'Moon',
        deityEn: 'Devi Gauri / Durga / Lord Krishna',
        deityHi: 'माता गौरी / श्री कृष्ण / ललिता त्रिपुरसुंदरी',
        mantraEn: 'Om Dum Durgayei Namaha / Om Namo Bhagavate Vasudevaya',
        mantraHi: 'ॐ दुं दुर्गायै नमः / ॐ नमो भगवते वासुदेवाय',
        twelfthSignD9,
        explanationEn: `12th from Karakamsha (${twelfthSignD9}) governed by Moon opens deep heart-centered devotion, compassion, and divine mother worship.`,
        explanationHi: `कारकांश से १२वें भाव में चंद्र का प्रभाव हृदय-स्थ भक्ति, करुणा एवं मातृ-शक्ति उपासना का संकेत है।`
      };
    case 'Mars':
      return {
        planetSignifier: 'Mars',
        deityEn: 'Lord Kartikeya / Skanda / Lord Hanuman / Narasimha',
        deityHi: 'भगवान कार्तिकेय (स्कंद) / श्री हनुमान / नरसिंह देव',
        mantraEn: 'Om Sharavana Bhavaya Namaha / Hanuman Chalisa',
        mantraHi: 'ॐ शरवणभवाय नमः / ॐ श्री हनुमते नमः',
        twelfthSignD9,
        explanationEn: `12th from Karakamsha (${twelfthSignD9}) governed by Mars awakens courageous spiritual tapas, overcoming inner obstacles, and fiery devotion.`,
        explanationHi: `कारकांश से १२वें भाव में मंगल का प्रभाव आंतरिक विकारों पर विजय, तपस्या एवं श्री हनुमान/स्कंद साधना का योग बनाता है।`
      };
    case 'Mercury':
      return {
        planetSignifier: 'Mercury',
        deityEn: 'Lord Vishnu / Narayana / Sri Rama',
        deityHi: 'भगवान विष्णु / श्री राम / नारायण',
        mantraEn: 'Om Vishnave Namaha / Vishnu Sahasranama',
        mantraHi: 'ॐ नमो नारायणाय / श्री राम रक्षा स्तोत्र',
        twelfthSignD9,
        explanationEn: `12th from Karakamsha (${twelfthSignD9}) governed by Mercury directs towards discernment, scriptural wisdom, and Vishnu devotion.`,
        explanationHi: `कारकांश से १२वें भाव में बुध का प्रभाव विवेक, स्वाध्याय एवं नारायण-भक्ति का शास्त्रीय प्रमाण है।`
      };
    case 'Jupiter':
      return {
        planetSignifier: 'Jupiter',
        deityEn: 'Sadguru / Lord Dakshinamurthy / Dattatreya / Brihaspati',
        deityHi: 'सद्गुरु / दक्षिणामूर्ति / भगवान दत्तात्रेय / देवाधिदेव',
        mantraEn: 'Om Gurave Namaha / Om Namo Bhagavate Dakshinamurtaye',
        mantraHi: 'ॐ गुरवे नमः / ॐ दक्षिणामूर्तये नमः',
        twelfthSignD9,
        explanationEn: `12th from Karakamsha (${twelfthSignD9}) governed by Jupiter confirms reverence for enlightened masters, Vedic wisdom, and Sadguru upasana.`,
        explanationHi: `कारकांश से १२वें भाव में गुरु का प्रभाव गुरु-कृपा, वैदिक चिंतन एवं दत्तात्रेय/दक्षिणामूर्ति उपासना का सर्वश्रेष्ठ आधार है।`
      };
    case 'Venus':
      return {
        planetSignifier: 'Venus',
        deityEn: 'Mahalakshmi / Sri Radha / Annapurna Devi',
        deityHi: 'महालक्ष्मी / श्री राधा रानी / अन्नपूर्णा देवी',
        mantraEn: 'Om Shreem Maha Lakshmyai Namaha',
        mantraHi: 'ॐ श्रीं ह्रीं क्लीं श्री सिद्ध लक्ष्म्यै नमः',
        twelfthSignD9,
        explanationEn: `12th from Karakamsha (${twelfthSignD9}) governed by Venus fosters love of sacred beauty, surrender through purity, and Lakshmi grace.`,
        explanationHi: `कारकांश से १२वें भाव में शुक्र का प्रभाव पवित्र सौंदर्य-बोध, आत्म-समर्पण एवं महालक्ष्मी की अनुकंपा दर्शाता है।`
      };
    case 'Saturn':
      return {
        planetSignifier: 'Saturn',
        deityEn: 'Lord Mahakala / Bhairava / Lord Shani / Kurma',
        deityHi: 'भगवान महाकाल / काल भैरव / शनि देव / कूर्म अवतार',
        mantraEn: 'Om Sham Shanaishcharaya Namaha / Maha Mrityunjaya',
        mantraHi: 'ॐ त्र्यम्बकं यजामहे सुगन्धिं पुष्टिवर्धनम् / ॐ शं शनैश्चराय नमः',
        twelfthSignD9,
        explanationEn: `12th from Karakamsha (${twelfthSignD9}) governed by Saturn channels endurance, austerity (vairagya), and profound Mahakala refuge.`,
        explanationHi: `कारकांश से १२वें भाव में शनि का प्रभाव गंभीर वैराग्य, कष्ट-शोधन एवं महाकाल/भैरव शरण की शास्त्रीय स्थिति है।`
      };
    case 'Rahu':
      return {
        planetSignifier: 'Rahu',
        deityEn: 'Goddess Durga / Chhinnamasta / Varaha Swamy',
        deityHi: 'मां दुर्गा / छिन्नमस्ता / श्री वराह स्वामी',
        mantraEn: 'Om Durgayei Namaha / Om Namo Bhagavate Varahaya',
        mantraHi: 'ॐ दुं दुर्गायै नमः / ॐ वराहाय नमः',
        twelfthSignD9,
        explanationEn: `12th from Karakamsha (${twelfthSignD9}) influenced by Rahu calls for dissolving worldly illusions through fierce Devi Durga refuge.`,
        explanationHi: `कारकांश से १२वें भाव में राहु का प्रभाव माया-जाल के भेदन हेतु मां भगवती दुर्गा की शरण का शास्त्रीय निर्देश देता है।`
      };
    case 'Ketu':
    default:
      return {
        planetSignifier: 'Ketu',
        deityEn: 'Lord Ganesha / Matsya Avatara / Nirguna Nirakara Brahman',
        deityHi: 'भगवान गणेश / मत्स्य अवतार / निर्गुण निराकार ब्रह्म',
        mantraEn: 'Om Gam Ganapataye Namaha / So-Ham',
        mantraHi: 'ॐ गं गणपतये नमः / सोऽहम्',
        twelfthSignD9,
        explanationEn: `12th from Karakamsha (${twelfthSignD9}) influenced by Ketu is the prime classical marker for Moksha (Kaivalya) and obstacle-dissolving Ganapati upasana.`,
        explanationHi: `कारकांश से १२वें भाव में केतु की स्थिति महर्षि जैमिनी के अनुसार कैवल्य (मोक्ष) एवं विघ्नहर्ता गणपति साधना का प्रमुख योग है।`
      };
  }
}

// ============================================================================
// HELPER: ACTIVE DASHA RESOLUTION ANCHORED TO FOCUS DATE
// ============================================================================
function resolveActiveDashaAtFocusDate(kundali: CompleteKundali, focusDate: Date): {
  maha: PlanetName;
  antar: PlanetName;
  pratyantar: PlanetName;
  startDate: string;
  endDate: string;
} {
  const targetTime = focusDate.getTime();
  
  if (kundali.dashas && kundali.dashas.length > 0) {
    for (const d of kundali.dashas) {
      const start = new Date(d.startDate).getTime();
      const end = new Date(d.endDate).getTime();
      if (targetTime >= start && targetTime <= end) {
        let activeAntar: PlanetName = 'Moon';
        let activePrat: PlanetName = 'Saturn';
        let aStart = d.startDate;
        let aEnd = d.endDate;

        if (d.antardashas && d.antardashas.length > 0) {
          for (const a of d.antardashas) {
            const aS = new Date(a.startDate).getTime();
            const aE = new Date(a.endDate).getTime();
            if (targetTime >= aS && targetTime <= aE) {
              activeAntar = a.planet as PlanetName;
              aStart = a.startDate;
              aEnd = a.endDate;
              break;
            }
          }
        }
        return {
          maha: d.planet as PlanetName,
          antar: activeAntar,
          pratyantar: activePrat,
          startDate: aStart,
          endDate: aEnd
        };
      }
    }
  }

  // Fallback to current/first dasha
  const d0 = kundali.dashas?.[0];
  return {
    maha: (d0?.planet || 'Jupiter') as PlanetName,
    antar: (d0?.antardashas?.[0]?.planet || 'Moon') as PlanetName,
    pratyantar: 'Saturn',
    startDate: d0?.startDate || '2024-01-01',
    endDate: d0?.endDate || '2030-01-01'
  };
}

// ============================================================================
// MASTER CALCULATION: SIME V1.0 (VEDANGJYOTISHAM V6.x)
// ============================================================================
export function calculateSpiritualityIntelligence(
  kundali: CompleteKundali,
  lang: Language = 'en',
  options?: {
    focusDate?: Date | string;
    horizon?: string;
    coordinates?: PanchangaCoordinates;
  }
): MasterSpiritualityResult {
  const isHi = lang === 'hi';
  const effectiveFocusDate = options?.focusDate
    ? new Date(options.focusDate)
    : new Date();
  const focusDateStr = effectiveFocusDate.toISOString().split('T')[0];

  // 1. Basic D1 & House Extraction
  const lagnaRasi = (kundali.houses?.[0]?.sign || kundali.planets?.[0]?.sign || 'Aries') as ZodiacSign;
  const lagnaLord = getSignLord(lagnaRasi);
  const lagnaLordPl = kundali.planets?.find(p => p.name === lagnaLord);
  const lagnaLordHouse = lagnaLordPl?.house || 1;

  // 9th House (Dharma, Guru, Higher Truth)
  const h9Rasi = (kundali.houses?.[8]?.sign || 'Sagittarius') as ZodiacSign;
  const h9Lord = getSignLord(h9Rasi);
  const h9LordPl = kundali.planets?.find(p => p.name === h9Lord);
  const h9LordHouse = h9LordPl?.house || 9;
  const h9Occupants = (kundali.planets || []).filter(p => p.house === 9).map(p => p.name as PlanetName);

  // 12th House (Moksha, Samadhi, Detachment, Solitude)
  const h12Rasi = (kundali.houses?.[11]?.sign || 'Pisces') as ZodiacSign;
  const h12Lord = getSignLord(h12Rasi);
  const h12LordPl = kundali.planets?.find(p => p.name === h12Lord);
  const h12LordHouse = h12LordPl?.house || 12;
  const h12Occupants = (kundali.planets || []).filter(p => p.house === 12).map(p => p.name as PlanetName);

  // 5th House (Purva Punya, Mantra, Discrimination)
  const h5Rasi = (kundali.houses?.[4]?.sign || 'Leo') as ZodiacSign;
  const h5Lord = getSignLord(h5Rasi);
  const h5LordPl = kundali.planets?.find(p => p.name === h5Lord);
  const h5LordHouse = h5LordPl?.house || 5;
  const h5Occupants = (kundali.planets || []).filter(p => p.house === 5).map(p => p.name as PlanetName);

  // 8th House (Tantra, Transformation, Esoteric Inquiry)
  const h8Rasi = (kundali.houses?.[7]?.sign || 'Scorpio') as ZodiacSign;
  const h8Lord = getSignLord(h8Rasi);
  const h8LordPl = kundali.planets?.find(p => p.name === h8Lord);
  const h8LordHouse = h8LordPl?.house || 8;
  const h8Occupants = (kundali.planets || []).filter(p => p.house === 8).map(p => p.name as PlanetName);

  // 10th House (Karma, Profession, Public Dharma)
  const h10Rasi = (kundali.houses?.[9]?.sign || 'Capricorn') as ZodiacSign;
  const h10Lord = getSignLord(h10Rasi);
  const h10LordPl = kundali.planets?.find(p => p.name === h10Lord);
  const h10LordHouse = h10LordPl?.house || 10;
  const h10Occupants = (kundali.planets || []).filter(p => p.house === 10).map(p => p.name as PlanetName);

  // Key Individual Planets
  const jupiterPl = kundali.planets?.find(p => p.name === 'Jupiter');
  const ketuPl = kundali.planets?.find(p => p.name === 'Ketu');
  const rahuPl = kundali.planets?.find(p => p.name === 'Rahu');
  const saturnPl = kundali.planets?.find(p => p.name === 'Saturn');
  const sunPl = kundali.planets?.find(p => p.name === 'Sun');
  const moonPl = kundali.planets?.find(p => p.name === 'Moon');
  const mercuryPl = kundali.planets?.find(p => p.name === 'Mercury');
  const venusPl = kundali.planets?.find(p => p.name === 'Venus');
  const marsPl = kundali.planets?.find(p => p.name === 'Mars');

  // Moon Sign and Nakshatra
  const moonSign = (moonPl?.sign || 'Cancer') as ZodiacSign;
  const moonNakshatra = moonPl?.nakshatra || 'Pushya';

  // Jaimini Karakas
  const { ak, akLong } = determineAtmakarakaAndAmatyakaraka(kundali);
  const akPl = kundali.planets?.find(p => p.name === ak);
  const akHouse = akPl?.house || 1;
  const akSign = (akPl?.sign || 'Aries') as ZodiacSign;
  const karakamsha = getKarakamshaSign(kundali, ak);
  const { bk, bkLong } = determineBhratrikaraka(kundali);
  const bkPl = kundali.planets?.find(p => p.name === bk);
  const bkHouse = bkPl?.house || 9;

  // Real Ishta Devata derived from Jaimini Upadesha Sutras
  const ishtaDevata = determineIshtaDevata(kundali, ak);

  // D-20 (Vimsamsha) Factors
  const d20Chart = kundali.charts?.D20;
  const d20Jupiter = d20Chart?.planets?.find(p => p.name === 'Jupiter');
  const d20Ketu = d20Chart?.planets?.find(p => p.name === 'Ketu');
  const d20Ak = d20Chart?.planets?.find(p => p.name === ak);

  // Dasha at Focus Date
  const dashaAtFocus = resolveActiveDashaAtFocusDate(kundali, effectiveFocusDate);

  // Chronological Native Age
  const nativeAge = calculateNativeAgeInYears(kundali.birthDetails?.date, effectiveFocusDate);

  // Real Transits Relative to Moon and Lagna
  const transitRef = getReferenceTransitSigns(effectiveFocusDate);
  const jupiterTransitHouseLagna = getHouseFromSign(lagnaRasi, transitRef.jupiterSign);
  const jupiterTransitHouseMoon = getHouseFromSign(moonSign, transitRef.jupiterSign);
  const saturnTransitHouseLagna = getHouseFromSign(lagnaRasi, transitRef.saturnSign);
  const saturnTransitHouseMoon = getHouseFromSign(moonSign, transitRef.saturnSign);
  const ketuTransitHouseLagna = getHouseFromSign(lagnaRasi, transitRef.ketuSign);
  const ketuTransitHouseMoon = getHouseFromSign(moonSign, transitRef.ketuSign);

  // Dominant Elemental Constitution
  const lagnaElement = getSignsElement(lagnaRasi);
  const moonElement = getSignsElement(moonSign);
  const sunElement = sunPl ? getSignsElement(sunPl.sign as ZodiacSign) : 'FIRE';

  // --------------------------------------------------------------------------
  // 1. DIMENSION 5.1: DYNAMIC SPIRITUAL INCLINATION CONVERGENCE
  // --------------------------------------------------------------------------
  let rawConvergenceScore = 15; // baseline human spiritual receptivity
  const evidenceItems: SpiritualityEvidenceItem[] = [];

  // Factor 1: Jupiter in Dharma/Moksha Bhava or aspecting Lagna
  if (jupiterPl) {
    if ([1, 5, 9, 12].includes(jupiterPl.house)) {
      rawConvergenceScore += 22;
      evidenceItems.push({
        factor: `Jupiter in House ${jupiterPl.house} (${jupiterPl.sign})`,
        calculatedValue: `House ${jupiterPl.house} (${jupiterPl.sign})`,
        jyotishRule: 'Jupiter in Trikona or 12th amplifies philosophical wisdom, dharmic integrity, and receptivity to sacred learning.',
        sourceShastra: 'BPHS Ch. 24, Sloka 31',
        evidenceStrength: 'ESSENTIAL',
        btrSensitivity: 'STABLE_ACROSS_BTR',
        interpretationEn: `Jupiter placed in House ${jupiterPl.house} provides an innate natural orientation toward higher learning, moral dignity, and authentic teachers.`,
        interpretationHi: `देवगुरु बृहस्पति का ${jupiterPl.house}वें भाव में होना उच्च ज्ञान, सदाचार एवं सद्गुरु कृपा का शास्त्रीय योग बनाता है।`
      });
    } else if ([4, 7, 10].includes(jupiterPl.house)) {
      rawConvergenceScore += 12;
      evidenceItems.push({
        factor: `Jupiter Kendra Fortitude (House ${jupiterPl.house})`,
        calculatedValue: `House ${jupiterPl.house}`,
        jyotishRule: 'Jupiter in Kendras anchors the native in ethical behavior and philosophical balance across worldly engagements.',
        sourceShastra: 'Saravali Ch. 34',
        evidenceStrength: 'SUPPORTING',
        btrSensitivity: 'STABLE_ACROSS_BTR',
        interpretationEn: `Jupiter anchoring a cardinal house infuses daily affairs with dharmic uprightness and sound conscience.`,
        interpretationHi: `केंद्र में स्थित बृहस्पति दैनिक जीवन में धर्म एवं न्यायप्रियता का सुदृढ़ संबल प्रदान करता है।`
      });
    }
  }

  // Factor 2: Ketu (Moksha Karaka) placement
  if (ketuPl) {
    if ([12, 9, 8, 4].includes(ketuPl.house)) {
      rawConvergenceScore += 24;
      evidenceItems.push({
        factor: `Ketu in Moksha/Inner Bhava (House ${ketuPl.house})`,
        calculatedValue: `House ${ketuPl.house} (${ketuPl.sign})`,
        jyotishRule: 'Ketu in 12th or 8th/9th acts as prime Moksha Karaka, initiating subtle inward detachment and contemplative depth.',
        sourceShastra: 'Jaimini Upadesha Sutras 1.2.69',
        evidenceStrength: 'ESSENTIAL',
        btrSensitivity: 'STABLE_ACROSS_BTR',
        interpretationEn: `Ketu positioned in House ${ketuPl.house} stimulates a natural affinity for inner stillness, meditation, and non-material contemplation.`,
        interpretationHi: `केतु का ${ketuPl.house}वें भाव में होना अंतर्मुखी शांति एवं भौतिक आसक्तियों से सहज विरक्ति का योग बनाता है।`
      });
    } else if (ketuPl.house === 5 || ketuPl.house === 1) {
      rawConvergenceScore += 14;
      evidenceItems.push({
        factor: `Ketu in Dharma Trikona (House ${ketuPl.house})`,
        calculatedValue: `House ${ketuPl.house}`,
        jyotishRule: 'Ketu in Dharma houses brings sharp intuitive discernment and sudden flashes of spiritual insight.',
        sourceShastra: 'BPHS Ch. 24',
        evidenceStrength: 'SUPPORTING',
        btrSensitivity: 'STABLE_ACROSS_BTR',
        interpretationEn: `Ketu in House ${ketuPl.house} sharpens intuitive perception and past-life spiritual impressions (Purva Samskaras).`,
        interpretationHi: `केतु का ${ketuPl.house}वें भाव में होना पूर्व-जन्म के संस्कारों एवं तीव्र अंतःप्रज्ञा को दर्शाता है।`
      });
    }
  }

  // Factor 3: 9th & 12th House Lords
  if ([1, 5, 9, 12].includes(h9LordHouse)) {
    rawConvergenceScore += 16;
    evidenceItems.push({
      factor: `9th Lord (${h9Lord}) in House ${h9LordHouse}`,
      calculatedValue: `House ${h9LordHouse}`,
      jyotishRule: 'Dharmesh in Trikona or 12th ensures enduring inclination toward truth, ethics, and philosophical study.',
      sourceShastra: 'BPHS Ch. 24, Sloka 35',
      evidenceStrength: 'ESSENTIAL',
      btrSensitivity: 'STABLE_ACROSS_BTR',
      interpretationEn: `The 9th lord situated in House ${h9LordHouse} creates an enduring moral compass and lifelong interest in sacred teachings.`,
      interpretationHi: `भाग्येश/धर्मेश का ${h9LordHouse}वें भाव में होना स्थायी नैतिक मार्गदर्शन एवं आध्यात्मिक रुचि सुनिश्चित करता है।`
    });
  }

  if ([1, 5, 9, 12].includes(h12LordHouse)) {
    rawConvergenceScore += 14;
    evidenceItems.push({
      factor: `12th Lord (${h12Lord}) in House ${h12LordHouse}`,
      calculatedValue: `House ${h12LordHouse}`,
      jyotishRule: 'Vyayesh connected with Dharma/Moksha houses directs expenditure of consciousness towards liberation and philanthropy.',
      sourceShastra: 'Phaladeepika Ch. 15',
      evidenceStrength: 'SUPPORTING',
      btrSensitivity: 'STABLE_ACROSS_BTR',
      interpretationEn: `The 12th lord in House ${h12LordHouse} facilitates detachment from petty disputes and fosters meditative mental quietude.`,
      interpretationHi: `द्वादशेश का ${h12LordHouse}वें भाव में होना मानसिक शांति एवं आत्मिक विरक्ति को सहजता से पोषित करता है।`
    });
  }

  // Factor 4: Atmakaraka (AK) placement & Karakamsha
  if ([1, 5, 9, 8, 12].includes(akHouse)) {
    rawConvergenceScore += 15;
    evidenceItems.push({
      factor: `Atmakaraka ${ak} in Dharma/Moksha House ${akHouse}`,
      calculatedValue: `House ${akHouse} (${akSign})`,
      jyotishRule: 'Atmakaraka in Dharma or Moksha sthanas indicates that soul evolution is prioritized in this incarnation.',
      sourceShastra: 'Jaimini Sutras 2.1',
      evidenceStrength: 'ESSENTIAL',
      btrSensitivity: 'MODERATELY_BTR_SENSITIVE',
      interpretationEn: `Atmakaraka ${ak} residing in House ${akHouse} establishes inner spiritual growth as the primary curriculum of your soul.`,
      interpretationHi: `आत्मकारक ${ak} का ${akHouse}वें भाव में होना आत्म-विकास को जीवन का सर्वोपरि ध्येय बनाता है।`
    });
  }

  // Factor 5: D-20 (Vimsamsha) Validation
  if (d20Jupiter || d20Ketu || d20Ak) {
    rawConvergenceScore += 12;
    evidenceItems.push({
      factor: 'D-20 Vimsamsha Upasana Confirmation',
      calculatedValue: 'Vimsamsha Auspicious Alignment',
      jyotishRule: 'Strong benefic or Ketu presence in D-20 confirms receptivity to upasana, mantra, and structured meditation.',
      sourceShastra: 'BPHS Ch. 7, Vimsamsha Adhyaya',
      evidenceStrength: 'SUPPORTING',
      btrSensitivity: 'HIGHLY_BTR_SENSITIVE',
      interpretationEn: `The spiritual divisional chart (D-20) validates your capacity for structured upasana and serene contemplation.`,
      interpretationHi: `विंशांश चक्र (D-20) नियमित उपासना, मंत्र-जप एवं ध्यान की सहज अनुकूलता को प्रमाणित करता है।`
    });
  }

  // Classify Spiritual Inclination based on actual convergence
  let inclinationClass: SpiritualInclinationClassification = 'MEANINGFUL';
  let inclinationHeadlineEn = 'Meaningful Contemplative Balance';
  let inclinationHeadlineHi = 'सार्थक दार्शनिक एवं नैतिक संतुलन';
  let inclinationSummaryEn = `Your horoscope exhibits steady, genuine spiritual markers centered on ethical discernment and quiet contemplation, unfolding gracefully over time.`;
  let inclinationSummaryHi = `आपकी कुंडली में विवेक, सदाचार एवं आत्म-विकास के संतुलित शास्त्रीय संकेत हैं, जो समय के साथ परिपक्व होते हैं।`;
  let innateVsDevelopedEn = `Innate contemplative foundation that matures through worldly experience and disciplined reflection.`;
  let innateVsDevelopedHi = `सहज अंतर्मुखी संस्कार, जो सांसारिक अनुभवों एवं स्वाध्याय के साथ और अधिक प्रगाढ़ होते हैं।`;

  if (rawConvergenceScore >= 75) {
    inclinationClass = 'HIGHLY_PROMINENT';
    inclinationHeadlineEn = 'Highly Prominent Spiritual Orientation';
    inclinationHeadlineHi = 'प्रगाढ़ आध्यात्मिक अभिरुचि व साधना योग';
    inclinationSummaryEn = `Multiple prime Moksha and Dharma factors converge strongly in your chart (including ${ak} Atmakaraka, Jupiter, and Ketu alignments), making the pursuit of truth and inner liberation a central life current.`;
    inclinationSummaryHi = `धर्म एवं मोक्ष भावों का सशक्त संमिलन (${ak} आत्मकारक, गुरु एवं केतु के प्रभाव सहित) आपके जीवन में सत्य-शोध एवं आंतरिक स्वतंत्रता को एक मुख्य प्रेरक धारा बनाता है।`;
    innateVsDevelopedEn = 'Strong innate spiritual sensitivity present from early life, demanding authentic contemplation.';
    innateVsDevelopedHi = 'प्रारंभिक आयु से ही सहज दार्शनिक जिज्ञासा एवं आध्यात्मिक संवेदनशीलता का प्रभाव।';
  } else if (rawConvergenceScore >= 52) {
    inclinationClass = 'STRONG';
    inclinationHeadlineEn = 'Strong Philosophical & Contemplative Emphasis';
    inclinationHeadlineHi = 'सुदृढ़ दार्शनिक एवं अंतर्मुखी चेतना';
    inclinationSummaryEn = `Your chart places notable emphasis on contemplative inquiry, duty-bound spirituality, and inner observation alongside active worldly responsibilities.`;
    inclinationSummaryHi = `सांसारिक उत्तरदायित्वों के साथ-साथ आपकी चेतना में आत्म-निरीक्षण एवं वैचारिक गहराई का सुदृढ़ स्थान है।`;
    innateVsDevelopedEn = 'Developed through reflective maturity, ethical integrity, and study of higher wisdom.';
    innateVsDevelopedHi = 'विवेकपूर्ण परिपक्वता, नैतिक आचरण एवं उत्तम साहित्य के अध्ययन से विकसित होता है।';
  } else if (rawConvergenceScore <= 35) {
    inclinationClass = 'EMERGING';
    inclinationHeadlineEn = 'Emerging & Seasonally Activated Spirituality';
    inclinationHeadlineHi = 'समय-सापेक्ष एवं विकासशील आध्यात्मिक चेतना';
    inclinationSummaryEn = `Spiritual themes are not rigid from birth, but activate meaningfully during specific Dasha transitions, pilgrimages, and life milestones.`;
    inclinationSummaryHi = `आध्यात्मिक आयाम जन्मजात रूप से अत्यधिक मुखर नहीं है, किंतु विशिष्ट दशाओं, तीर्थाटन एवं जीवन के मोड़ों पर जाग्रत होता है।`;
    innateVsDevelopedEn = 'Awakened progressively through life transitions, crisis points, and philosophical questioning.';
    innateVsDevelopedHi = 'जीवन के अनुभवों, चिंतन एवं विशिष्ट काल-खंडों के माध्यम से क्रमिक रूप से विकसित होता है।';
  }

  // --------------------------------------------------------------------------
  // 2. DIMENSION 5.2: DYNAMIC ORIENTATION SCORING (JNANA, BHAKTI, DHYANA, ETC.)
  // --------------------------------------------------------------------------
  const orientationScores: Record<SpiritualOrientationType, number> = {
    JNANA: 10,
    BHAKTI: 10,
    DHYANA: 10,
    KARMA_SEVA: 10,
    YOGA: 8,
    MANTRA: 8,
    DHARMA: 12,
    VAIRAGYA: 8,
    MOKSHA: 8
  };

  // Specific Graha & Bhava additions:
  // Jñāna (Knowledge & Discernment)
  if (jupiterPl && [1, 5, 9].includes(jupiterPl.house)) orientationScores.JNANA += 28;
  if (mercuryPl && [1, 5, 9].includes(mercuryPl.house)) orientationScores.JNANA += 24;
  if (sunPl && [1, 5, 9].includes(sunPl.house)) orientationScores.JNANA += 18;
  if (['Gemini', 'Virgo', 'Sagittarius', 'Aquarius'].includes(lagnaRasi)) orientationScores.JNANA += 15;
  if (ak === 'Jupiter' || ak === 'Mercury') orientationScores.JNANA += 20;

  // Bhakti (Devotion & Faith)
  if (moonPl && [4, 5, 9, 12].includes(moonPl.house)) orientationScores.BHAKTI += 28;
  if (venusPl && [4, 5, 9, 12].includes(venusPl.house)) orientationScores.BHAKTI += 24;
  if (['Cancer', 'Pisces', 'Taurus'].includes(moonSign)) orientationScores.BHAKTI += 18;
  if (ak === 'Moon' || ak === 'Venus') orientationScores.BHAKTI += 22;

  // Dhyāna (Meditation & Stillness)
  if (ketuPl && [8, 12, 9, 4].includes(ketuPl.house)) orientationScores.DHYANA += 32;
  if (saturnPl && [8, 12].includes(saturnPl.house)) orientationScores.DHYANA += 22;
  if (['Pisces', 'Scorpio', 'Aquarius'].includes(lagnaRasi) || ['Pisces', 'Scorpio', 'Aquarius'].includes(moonSign)) orientationScores.DHYANA += 16;
  if (h12Occupants.length > 0) orientationScores.DHYANA += 18;
  if (ak === 'Saturn' || ak === 'Ketu') orientationScores.DHYANA += 20;

  // Karma & Sevā (Righteous Action & Duty)
  if (saturnPl && [1, 6, 10, 11].includes(saturnPl.house)) orientationScores.KARMA_SEVA += 30;
  if (h10LordHouse === 9 || h10LordHouse === 1 || h10LordHouse === 5) orientationScores.KARMA_SEVA += 22;
  if (['Capricorn', 'Virgo', 'Aries', 'Taurus'].includes(lagnaRasi)) orientationScores.KARMA_SEVA += 16;
  if (ak === 'Saturn' || ak === 'Mars') orientationScores.KARMA_SEVA += 18;

  // Dharma (Righteousness & Moral Order)
  if (h9LordHouse === 9 || h9LordHouse === 1 || h9LordHouse === 5) orientationScores.DHARMA += 26;
  if (sunPl && [1, 9, 10].includes(sunPl.house)) orientationScores.DHARMA += 22;
  if (['Leo', 'Sagittarius', 'Aries'].includes(lagnaRasi)) orientationScores.DHARMA += 16;
  if (ak === 'Sun' || ak === 'Jupiter') orientationScores.DHARMA += 20;

  // Yoga & Prāṇāyāma (Psycho-physical discipline)
  if (marsPl && [1, 10, 8].includes(marsPl.house)) orientationScores.YOGA += 26;
  if (sunPl && [1, 10].includes(sunPl.house)) orientationScores.YOGA += 18;
  if (lagnaElement === 'FIRE') orientationScores.YOGA += 15;
  if (ak === 'Mars') orientationScores.YOGA += 22;

  // Mantra (Sacred Sound & Japa)
  if (h5Occupants.length > 0 || [1, 5, 9].includes(h5LordHouse)) orientationScores.MANTRA += 28;
  if (mercuryPl && [2, 5].includes(mercuryPl.house)) orientationScores.MANTRA += 20;
  if (jupiterPl && [2, 5].includes(jupiterPl.house)) orientationScores.MANTRA += 20;

  // Vairāgya (Dispassion & Detachment)
  if (saturnPl && ketuPl && (saturnPl.house === 12 || ketuPl.house === 12)) orientationScores.VAIRAGYA += 32;
  else if (saturnPl?.house === 12 || ketuPl?.house === 12) orientationScores.VAIRAGYA += 22;
  if (h12LordHouse === 12 || h12LordHouse === 8) orientationScores.VAIRAGYA += 18;

  // Mokṣa (Liberation & Dissolution of Ego)
  if (h12Occupants.includes('Ketu') || h12Occupants.includes('Jupiter')) orientationScores.MOKSHA += 32;
  else if (h12Occupants.length > 0) orientationScores.MOKSHA += 20;
  if (['Pisces', 'Scorpio'].includes(karakamsha.signEn)) orientationScores.MOKSHA += 22;

  // Rank orientations dynamically
  const sortedOrientations = (Object.keys(orientationScores) as SpiritualOrientationType[]).sort(
    (a, b) => orientationScores[b] - orientationScores[a]
  );

  const primaryOrientType = sortedOrientations[0];
  const secondaryOrientTypes = [sortedOrientations[1], sortedOrientations[2]];

  const getOrientationDetails = (type: SpiritualOrientationType, isPrimary: boolean, isSecondary: boolean): SpiritualOrientationItem => {
    switch (type) {
      case 'JNANA':
        return {
          type,
          nameEn: 'Jñāna — Knowledge, Inquiry & Discernment',
          nameHi: 'ज्ञान योग — विचार, विवेक एवं आत्म-अनुसंधान',
          sanskritTerm: 'ज्ञान मार्ग (Jñāna Mārga)',
          convergenceScore: Math.min(98, orientationScores.JNANA),
          isPrimary,
          isSecondary,
          descriptionEn: 'Truth approached through philosophical inquiry, scriptural study (Svādhyāya), and continuous discernment between the transient and the permanent (Viveka).',
          descriptionHi: 'दार्शनिक चिंतन, स्वाध्याय एवं नित्य-अनित्य के विवेक द्वारा सत्य का अनुसंधान।',
          keyClassicalTriggers: ['Jupiter/Mercury alignment in Dharma Trikonas', '5th/9th Bhava intellectual acuity']
        };
      case 'DHYANA':
        return {
          type,
          nameEn: 'Dhyāna — Meditation, Contemplation & Inner Stillness',
          nameHi: 'ध्यान योग — मौन, अंतर्मुखी स्थिरता व समाधि चेतना',
          sanskritTerm: 'ध्यान मार्ग (Dhyāna Mārga)',
          convergenceScore: Math.min(98, orientationScores.DHYANA),
          isPrimary,
          isSecondary,
          descriptionEn: 'Spiritual alignment through silent contemplation, steadying the mind (Chitta Vritti Nirodha), and non-reactive self-observation.',
          descriptionHi: 'मौन साधना, चित्त की वृत्तियों का निरोध एवं साक्षी भाव से आत्म-निरीक्षण।',
          keyClassicalTriggers: ['Ketu in Moksha Trikona', '12th House quietude']
        };
      case 'BHAKTI':
        return {
          type,
          nameEn: 'Bhakti — Devotion, Surrender & Faith',
          nameHi: 'भक्ति योग — समर्पण, श्रद्धा एवं ईश-प्रीति',
          sanskritTerm: 'भक्ति मार्ग (Bhakti Mārga)',
          convergenceScore: Math.min(98, orientationScores.BHAKTI),
          isPrimary,
          isSecondary,
          descriptionEn: 'Unfolding inner peace through devotional connection, sincere prayer, surrender of ego (Sharanagati), and heartfelt sacred resonance.',
          descriptionHi: 'ईश्वर के प्रति श्रद्धा, भावपूर्ण समर्पण एवं अहंकार के विसर्जन से आत्मिक शांति।',
          keyClassicalTriggers: ['Moon/Venus in receptive houses', '5th Bhava Ishta alignment']
        };
      case 'KARMA_SEVA':
        return {
          type,
          nameEn: 'Karma & Sevā — Selfless Action & Service',
          nameHi: 'कर्म योग — निःस्वार्थ सेवा, कर्तव्य-पालन एवं लोक-कल्याण',
          sanskritTerm: 'कर्म व सेवा मार्ग (Karma-Sevā)',
          convergenceScore: Math.min(98, orientationScores.KARMA_SEVA),
          isPrimary,
          isSecondary,
          descriptionEn: 'Spirituality lived through disciplined duty, serving humanity without obsessive attachment to outcomes (Nishkama Karma).',
          descriptionHi: 'फल की आसक्ति से मुक्त होकर लोक-कल्याण एवं निष्काम कर्तव्य-साधना।',
          keyClassicalTriggers: ['Saturn-Lagnesh dignified interplay', '10th/6th house service focus']
        };
      case 'DHARMA':
        return {
          type,
          nameEn: 'Dharma — Responsibility & Righteous Action',
          nameHi: 'धर्म निष्ठा — कर्तव्य, सत्य-आचरण एवं मर्यादा',
          sanskritTerm: 'धर्म मार्ग (Dharma Mārga)',
          convergenceScore: Math.min(98, orientationScores.DHARMA),
          isPrimary,
          isSecondary,
          descriptionEn: 'Spiritual development expressed through upholding family commitments, social ethics, and truthful conduct in daily responsibilities.',
          descriptionHi: 'पारिवारिक दायित्वों का निर्वहन, सत्यनिष्ठा एवं मर्यादापूर्ण आचरण ही साधना का रूप।',
          keyClassicalTriggers: ['9th Lord fortitude', 'Jupiter-Sun solar dignity']
        };
      case 'VAIRAGYA':
        return {
          type,
          nameEn: 'Vairāgya — Detachment & Simplification',
          nameHi: 'वैराग्य — सादगी, अनासक्ति एवं मानसिक स्वतंत्रता',
          sanskritTerm: 'वैराग्य मार्ग (Vairāgya)',
          convergenceScore: Math.min(98, orientationScores.VAIRAGYA),
          isPrimary,
          isSecondary,
          descriptionEn: 'Gradual freedom from unnecessary material identification, embracing simplicity, emotional self-sufficiency, and inner independence.',
          descriptionHi: 'अनावश्यक भौतिक लालसाओं से मुक्ति, सादगी और आंतरिक स्वाधीनता।',
          keyClassicalTriggers: ['Saturn/Ketu in 12th/8th axis', 'Moksha bhava maturity']
        };
      case 'MANTRA':
        return {
          type,
          nameEn: 'Mantra — Sacred Sound & Japa Practice',
          nameHi: 'मंत्र साधना — जप, नाद अनुसंधान एवं स्तोत्र',
          sanskritTerm: 'मंत्र योग (Mantra Yoga)',
          convergenceScore: Math.min(98, orientationScores.MANTRA),
          isPrimary,
          isSecondary,
          descriptionEn: 'Aligning consciousness through rhythmic Vedic mantra recitation, sound vibration, and focused japa sadhana.',
          descriptionHi: 'वैदिक मंत्रों के लयबद्ध जप एवं नाद-ऊर्जा से मन का शोधन।',
          keyClassicalTriggers: ['5th House Purva Punya strength', 'Jupiter-Mercury speech harmony']
        };
      case 'YOGA':
        return {
          type,
          nameEn: 'Rāja Yoga — Breath Discipline & Mind Mastery',
          nameHi: 'राज योग व प्राणायाम — प्राण-संयम एवं अनुशासन',
          sanskritTerm: 'योग साधन (Yoga Mārga)',
          convergenceScore: Math.min(98, orientationScores.YOGA),
          isPrimary,
          isSecondary,
          descriptionEn: 'Integration of body, breath, and mind through asana, pranayama, and systematic meditative focus.',
          descriptionHi: 'प्राणायाम, आसन एवं मानसिक एकाग्रता द्वारा चेतना का ऊर्ध्वगमन।',
          keyClassicalTriggers: ['Mars-Saturn tapas axis', 'Lagna vitality']
        };
      case 'MOKSHA':
      default:
        return {
          type,
          nameEn: 'Mokṣa — Inner Freedom & Transcendence',
          nameHi: 'मोक्ष — आत्म-बोध एवं परम शांति',
          sanskritTerm: 'मोक्ष चेतना (Mokṣa Mārga)',
          convergenceScore: Math.min(98, orientationScores.MOKSHA),
          isPrimary,
          isSecondary,
          descriptionEn: 'A deep seeking for transcendence beyond worldly roles, recognizing the timeless essence of consciousness.',
          descriptionHi: 'सांसारिक भूमिकाओं से परे आत्म-स्वरूप की पहचान और शाश्वत शांति की खोज।',
          keyClassicalTriggers: ['12th House Ketu/Jupiter illumination', 'Karakamsha 12th disposition']
        };
    }
  };

  const allOrientationItems: SpiritualOrientationItem[] = sortedOrientations.map((t, idx) =>
    getOrientationDetails(t, idx === 0, idx === 1 || idx === 2)
  );

  // --------------------------------------------------------------------------
  // 3. DIMENSION 5.3: SYNTHESIZED SPIRITUAL PATH
  // --------------------------------------------------------------------------
  const p1 = allOrientationItems[0];
  const p2 = allOrientationItems[1];

  const getCleanLabelEn = (item: SpiritualOrientationItem) => {
    switch (item.type) {
      case 'JNANA': return 'Jñāna (Inquiry)';
      case 'DHYANA': return 'Dhyāna (Meditation)';
      case 'BHAKTI': return 'Bhakti (Devotion)';
      case 'KARMA_SEVA': return 'Karma-Sevā (Service)';
      case 'DHARMA': return 'Dharma (Righteousness)';
      case 'MANTRA': return 'Mantra (Sacred Sound)';
      case 'YOGA': return 'Rāja Yoga (Breath)';
      case 'VAIRAGYA': return 'Vairāgya (Simplicity)';
      case 'MOKSHA': return 'Mokṣa (Transcendence)';
    }
  };

  const getCleanLabelHi = (item: SpiritualOrientationItem) => {
    switch (item.type) {
      case 'JNANA': return 'ज्ञान योग';
      case 'DHYANA': return 'ध्यान योग';
      case 'BHAKTI': return 'भक्ति योग';
      case 'KARMA_SEVA': return 'कर्म योग';
      case 'DHARMA': return 'धर्म निष्ठा';
      case 'MANTRA': return 'मंत्र साधना';
      case 'YOGA': return 'राज योग';
      case 'VAIRAGYA': return 'वैराग्य साधना';
      case 'MOKSHA': return 'मोक्ष चेतना';
    }
  };

  const pathTitleEn = `${getCleanLabelEn(p1)} & ${getCleanLabelEn(p2)} Synthesis`;
  const pathTitleHi = `${getCleanLabelHi(p1)} एवं ${getCleanLabelHi(p2)} समन्वय`;
  const pathSynthesisEn = `Your chart naturally synthesizes ${p1.nameEn.split('—')[0].trim()} as the lead orientation with ${p2.nameEn.split('—')[0].trim()} as the supportive channel. Anchored by ${ak} Atmakaraka and ${h9Rasi} 9th house, your inner growth blossoms through authentic understanding supported by practical discipline rather than rigid dogmatism.`;
  const pathSynthesisHi = `आपकी कुंडली ${getCleanLabelHi(p1)} को मुख्य आधार तथा ${getCleanLabelHi(p2)} को सहायक साधन बनाकर चलने का संकेत देती है। आत्मकारक ${ak} एवं ${h9Rasi} नवम भाव के प्रभाव से यह यात्रा किसी संकीर्ण हठ के स्थान पर विवेक और व्यावहारिक अनुशासन से परिपक्व होती है।`;

  const spiritualPath: SpiritualPathResult = {
    pathTitleEn,
    pathTitleHi,
    synthesizedDescriptionEn: pathSynthesisEn,
    synthesizedDescriptionHi: pathSynthesisHi,
    primaryOrientation: primaryOrientType,
    secondaryOrientations: secondaryOrientTypes,
    isSingleDominant: p1.convergenceScore - p2.convergenceScore >= 25,
    isMixed: true,
    classicalFoundations: [
      `Primary orientation (${getCleanLabelEn(p1)}) supported by House ${jupiterPl?.house || 9} / ${ketuPl?.house || 12} placements.`,
      `Secondary orientation (${getCleanLabelEn(p2)}) supported by Lagnesh ${lagnaLord} in House ${lagnaLordHouse} and D-20 alignment.`
    ]
  };

  // --------------------------------------------------------------------------
  // 4. DIMENSION 5.4: SĀDHANĀ (SPIRITUAL PRACTICE)
  // Dynamically prioritized and tailored to Ishta Devata, 5th, 9th, and 12th houses
  // --------------------------------------------------------------------------
  const sadhanaCandidates: SadhanaPracticeResult[] = [
    {
      form: 'MANTRA_JAPA',
      nameEn: `Mantra Japa (${ishtaDevata.deityEn})`,
      nameHi: `मंत्र जप (${ishtaDevata.deityHi})`,
      compatibilityLevel: primaryOrientType === 'MANTRA' || h5Occupants.length > 0 || ishtaDevata.planetSignifier === ak ? 'HIGHLY_RESONANT' : 'FAVORABLE',
      guidanceEn: `Recitation of ${ishtaDevata.mantraEn} with steady, rhythmic mindfulness (108 repetitions daily).`,
      guidanceHi: `${ishtaDevata.mantraHi} का शांत, लयबद्ध एवं एकाग्र मन से दैनिक जप (१०८ माला)।`,
      astrologicalBasis: `Governed by 12th from Karakamsha (${ishtaDevata.twelfthSignD9}) and 5th Purva Punya sthana.`,
      practicalTimingGuidanceEn: 'Ideal during morning twilight or Brahma Muhurta (4:30 AM–5:45 AM).',
      practicalTimingGuidanceHi: 'ब्रह्म मुहूर्त अथवा सूर्योदय वेला में जप सर्वाधिक फलदायी।'
    },
    {
      form: 'DHYANA_MEDITATION',
      nameEn: 'Dhyāna / Breath & Silent Contemplation',
      nameHi: 'ध्यान व मौन आत्म-निरीक्षण',
      compatibilityLevel: [12, 8, 9].includes(ketuPl?.house || 0) || primaryOrientType === 'DHYANA' ? 'HIGHLY_RESONANT' : 'FAVORABLE',
      guidanceEn: '15–25 minutes of quiet, unhurried breath awareness (Pranava dhyana) observing the stillness behind thoughts.',
      guidanceHi: '१५-२५ मिनट मौन श्वास-निरीक्षण, विचारों के साक्षी भाव एवं आंतरिक शांति का नियमित अभ्यास।',
      astrologicalBasis: `Supported by Ketu in House ${ketuPl?.house || 12} and 12th house quietude.`,
      practicalTimingGuidanceEn: 'Most nourishing during dusk twilight (Sandhya) or post-sunset.',
      practicalTimingGuidanceHi: 'संध्याकाल अथवा रात्रि विश्राम से पूर्व उत्तम।'
    },
    {
      form: 'SVADHYAYA_SELF_STUDY',
      nameEn: 'Svādhyāya / Sacred Scriptural Study',
      nameHi: 'स्वाध्याय एवं दार्शनिक चिंतन',
      compatibilityLevel: primaryOrientType === 'JNANA' || [1, 5, 9].includes(jupiterPl?.house || 0) ? 'HIGHLY_RESONANT' : 'FAVORABLE',
      guidanceEn: 'Reading classic philosophical texts (Upanishads, Bhagavad Gita, Vivekachudamani) to ground the intellect in eternal discernment.',
      guidanceHi: 'उपनिषद, श्रीमद्भगवद्गीता अथवा विवेकचूड़ामणि का नित्य पठन मन को विवेकपूर्ण आधार देता है।',
      astrologicalBasis: `Fortified by 9th House (${h9Rasi}) and Jupiter in House ${jupiterPl?.house || 9}.`,
      practicalTimingGuidanceEn: 'Beneficial on quiet weekend mornings or Thursday mornings.',
      practicalTimingGuidanceHi: 'गुरुवार प्रातः अथवा शांत अवकाश के समय अध्ययन।'
    },
    {
      form: 'SEVA_SERVICE',
      nameEn: 'Sevā / Selfless Humanitarian Duty',
      nameHi: 'निःस्वार्थ सेवा एवं परोपकार',
      compatibilityLevel: primaryOrientType === 'KARMA_SEVA' || [6, 10].includes(saturnPl?.house || 0) ? 'HIGHLY_RESONANT' : 'FAVORABLE',
      guidanceEn: 'Directing skills, time, and resources to alleviate distress without demanding recognition or commercial return.',
      guidanceHi: 'बिना किसी मान-सम्मान की आकांक्षा के समाज व जरूरतमंदों की मूक सेवा एवं कर्तव्य-पालन।',
      astrologicalBasis: `Derived from Saturn in House ${saturnPl?.house || 10} and 10th house karmic responsibility.`,
      practicalTimingGuidanceEn: 'Integrating intentional acts of anonymous kindness into weekly routines.',
      practicalTimingGuidanceHi: 'साप्ताहिक दिनचर्या में सेवा कार्यों को सहजता से सम्मिलित करें।'
    },
    {
      form: 'YOGA_PRANAYAMA',
      nameEn: 'Prāṇāyāma & Rāja Yoga Discipline',
      nameHi: 'प्राणायाम व प्राण-संयम साधना',
      compatibilityLevel: primaryOrientType === 'YOGA' || lagnaElement === 'FIRE' ? 'HIGHLY_RESONANT' : 'SECONDARY',
      guidanceEn: 'Systematic Nadi Shodhana (alternate nostril breathing) and gentle asana sequences to balance solar and lunar subtle channels.',
      guidanceHi: 'नाड़ी शोधन प्राणायाम एवं सूर्य-चंद्र नाड़ियों के संतुलन हेतु नियमित योगाभ्यास।',
      astrologicalBasis: `Aligned with Lagna vitality (${lagnaRasi}) and Mars placement.`,
      practicalTimingGuidanceEn: 'Early morning on an empty stomach.',
      practicalTimingGuidanceHi: 'प्रातःकाल खाली पेट अभ्यास करें।'
    }
  ];

  // Sort sadhana by resonance level
  const sadhanaThemes = sadhanaCandidates.sort((a, b) => {
    const weight = (lvl: string) => lvl === 'HIGHLY_RESONANT' ? 3 : lvl === 'FAVORABLE' ? 2 : 1;
    return weight(b.compatibilityLevel) - weight(a.compatibilityLevel);
  });

  // --------------------------------------------------------------------------
  // 5. DIMENSION 5.5 & 5.6: INNER EXPERIENCE & DEVELOPMENTAL STAGE
  // Dynamically derived from chronological age and Atmakaraka curriculum
  // --------------------------------------------------------------------------
  let currentStage: SeekerPractitionerStage = 'PRACTITIONER';
  let stageNameEn = 'Sincere Practitioner & Worldly Integrator';
  let stageNameHi = 'सजग साधक एवं कर्म-योगी';
  let stageDescriptionEn = 'You are actively integrating ethical principles and quiet contemplative practice into daily existence.';
  let stageDescriptionHi = 'आप अपने दैनिक जीवन में मौन साधना एवं नैतिक सिद्धांतों को आचरण में ढाल रहे हैं।';
  let evolutionaryHorizonEn = 'Progressing toward quiet mentorship, where your lived calm benefits peers and family naturally.';
  let evolutionaryHorizonHi = 'क्रमशः उस स्थिति की ओर अग्रसर जहां आपकी सहज शांति और आचरण परिजनों एवं साथियों के लिए संबल बनेगा।';

  if (nativeAge < 26) {
    currentStage = 'SEEKER';
    stageNameEn = 'Inquisitive Seeker & Foundational Cultivator';
    stageNameHi = 'जिज्ञासु अन्वेषक व आधारभूत साधक';
    stageDescriptionEn = `In your mid-twenties (Age ${nativeAge}), your soul is actively exploring worldviews, testing philosophies, and laying the groundwork for authentic spiritual inquiry.`;
    stageDescriptionHi = `आपकी वर्तमान आयु (${nativeAge} वर्ष) जीवन-मूल्यों की खोज, दार्शनिक अध्ययन एवं आंतरिक आधारशिला रखने का काल है।`;
    evolutionaryHorizonEn = 'Transitioning from theoretical curiosity into disciplined daily contemplation.';
    evolutionaryHorizonHi = 'केवल वैचारिक जिज्ञासा से नियमित दैनिक साधना की ओर अग्रसर।';
  } else if (nativeAge >= 26 && nativeAge <= 44) {
    currentStage = 'PRACTITIONER';
    stageNameEn = 'Dedicated Practitioner Amidst Worldly Forge';
    stageNameHi = 'सजग साधक व कर्म-क्षेत्र में समत्व';
    stageDescriptionEn = `At Age ${nativeAge}, your primary spiritual laboratory is daily life: balancing career, family, and relationships without losing touch with your inner stillness.`;
    stageDescriptionHi = `वर्तमान आयु (${nativeAge} वर्ष) में सांसारिक कर्तव्यों, आजीविका और संबंधों के बीच आंतरिक शांति को बनाए रखना ही आपकी प्रमुख साधना है।`;
    evolutionaryHorizonEn = 'Cultivating unwavering poise (Samatvam) amidst worldly pressures.';
    evolutionaryHorizonHi = 'सांसारिक व्यस्तताओं के मध्य समत्व भाव और मानसिक स्थिरता का विकास।';
  } else if (nativeAge > 44 && nativeAge <= 62) {
    currentStage = 'INTEGRATION';
    stageNameEn = 'Mature Contemplative & Inward Deepener';
    stageNameHi = 'गहन अंतर्मुखी साधक व तत्व-दर्शी';
    stageDescriptionEn = `At Age ${nativeAge}, superficial worldly ambitions yield to authentic substance. You naturally gravitate toward simplification, deep reflection, and lasting legacy.`;
    stageDescriptionHi = `वर्तमान आयु (${nativeAge} वर्ष) में दिखावटी दौड़ समाप्त होकर आंतरिक सादगी, गहन ध्यान और स्थायी जीवन-सार पर ध्यान केंद्रित होता है।`;
    evolutionaryHorizonEn = 'Effortless inner freedom and becoming a quiet beacon of calm for others.';
    evolutionaryHorizonHi = 'सहज अनासक्ति एवं दूसरों के लिए मूक संबल और प्रेरणा बनना।';
  } else {
    currentStage = 'CONTRIBUTION';
    stageNameEn = 'Wise Elder & Transcendent Anchor';
    stageNameHi = 'प्रबुद्ध मार्गदर्शक व शांत साक्षी';
    stageDescriptionEn = `In this seasoned phase of life (Age ${nativeAge}), personal ego attachments recede. You function as a tranquil anchor of wisdom, benevolence, and sacred presence.`;
    stageDescriptionHi = `जीवन के इस परिपक्व चरण (${nativeAge} वर्ष) में व्यक्तिगत आग्रह शांत होकर व्यापक करुणा, आशीर्वाद एवं साक्षी भाव का उदय होता है।`;
    evolutionaryHorizonEn = 'Total surrender, peace with mortality, and abiding in timeless consciousness.';
    evolutionaryHorizonHi = 'पूर्ण समर्पण, निर्भयता एवं शाश्वत आत्म-शांति में स्थित होना।';
  }

  // Atmakaraka specific soul lesson
  const getAtmakarakaCurriculum = (karaka: PlanetName) => {
    switch (karaka) {
      case 'Sun':
        return {
          lessonEn: 'Ego transcendence, overcoming the need for applause, and discovering the humble divine light within.',
          lessonHi: 'अहंकार का विसर्जन, यश की लालसा से मुक्ति एवं आंतरिक आत्म-प्रकाश की पहचान।'
        };
      case 'Moon':
        return {
          lessonEn: 'Emotional equilibrium, mastering inner moods, and transforming personal clinging into unconditional compassion.',
          lessonHi: 'मानसिक समत्व, चंचलता पर नियंत्रण एवं मोह को विशुद्ध करुणा में रूपांतरित करना।'
        };
      case 'Mars':
        return {
          lessonEn: 'Mastering anger and impatience, cultivating non-violence (Ahimsa), and directing fire into spiritual tapas.',
          lessonHi: 'क्रोध व अधीरता पर विजय, अहिंसा का पालन एवं प्राण-ऊर्जा को साधना में लगाना।'
        };
      case 'Mercury':
        return {
          lessonEn: 'Disciplining the intellectual mind, avoiding argumentative pedantry, and honoring silent truth above clever words.',
          lessonHi: 'बौद्धिक वाद-विवाद से मुक्ति, वाणी का संयम एवं शब्दों से परे मौन सत्य का आदर।'
        };
      case 'Jupiter':
        return {
          lessonEn: 'Spiritual humility, eliminating sanctimonious judgment, and treating every being as a manifestation of the divine teacher.',
          lessonHi: 'आध्यात्मिक निरहंकारिता, दूसरों पर दोषारोपण न करना एवं सभी में ईश्वर का दर्शन।'
        };
      case 'Venus':
        return {
          lessonEn: 'Purity of desire, transmuting romantic or sensual longing into sacred, selfless devotion.',
          lessonHi: 'इंद्रिय संयम, वासना का उदात्तीकरण एवं विशुद्ध ईश-प्रेम की अनुभूति।'
        };
      case 'Saturn':
        return {
          lessonEn: 'Patience through sorrow or delay, enduring duty as sacred purification, and seeing grace in loneliness.',
          lessonHi: 'कठिनाइयों में अगाध धैर्य, कर्तव्य को ही तप मानना एवं एकांत में ईश्वर की समीपता पाना।'
        };
      case 'Rahu':
      default:
        return {
          lessonEn: 'Seeing through worldly glamour, overcoming restlessness, and finding grounded truth beyond deception.',
          lessonHi: 'सांसारिक आकर्षणों का भ्रम दूर करना एवं छल-कपट से परे वास्तविक शांति पाना।'
        };
    }
  };

  const akLesson = getAtmakarakaCurriculum(ak);

  const developmentalStage: SpiritualDevelopmentalStage = {
    currentStage,
    stageNameEn,
    stageNameHi,
    stageDescriptionEn,
    stageDescriptionHi,
    evolutionaryHorizonEn,
    evolutionaryHorizonHi
  };

  const spiritualExperienceThemes: SpiritualExperienceTheme[] = [
    {
      themeKey: 'contemplative_depth',
      titleEn: 'Contemplative Depth & Inner Solitude',
      titleHi: 'गहन विचारशीलता एवं एकांत-प्रीति',
      descriptionEn: `A recurring natural impulse to step back from social noise to process life experiences quietly and deeply (anchored by Ketu in House ${ketuPl?.house || 12}).`,
      descriptionHi: `सांसारिक कोलाहल से कुछ समय अलग होकर अपने अनुभवों पर शांति से विचार करने की स्वाभाविक वृत्ति।`,
      resonance: 'PROMINENT'
    },
    {
      themeKey: 'meaning_over_prestige',
      titleEn: 'Search for Meaning Over External Prestige',
      titleHi: 'दिखावे के स्थान पर वास्तविक सार्थकता की खोज',
      descriptionEn: `Growing indifference to hollow worldly titles, seeking authentic contribution and lasting inner peace instead.`,
      descriptionHi: `दिखावटी यश के प्रति उदासीनता और स्थायी आत्म-संतोष की ओर खिंचाव।`,
      resonance: 'PROMINENT'
    },
    {
      themeKey: 'reverence_for_life',
      titleEn: 'Reverence for Life & Ethical Duty',
      titleHi: 'जीवन के प्रति आदर एवं नैतिक मर्यादा',
      descriptionEn: `An inherent conviction that actions carry karmic weight, fostering compassionate responsibility.`,
      descriptionHi: `यह आंतरिक बोध कि प्रत्येक कर्म का फल होता है, जिससे उत्तरदायित्व और संवेदनशीलता बढ़ती है।`,
      resonance: 'SUPPORTED'
    }
  ];

  const transformationThemes: SpiritualTransformationTheme[] = [
    {
      fromStageEn: 'Restlessness & Anxious Proving',
      fromStageHi: 'अस्थिरता एवं निरंतर खुद को सिद्ध करने की होड़',
      toStageEn: 'Stillness & Focused Purpose',
      toStageHi: 'स्थिरता एवं शांत कर्तव्य-बोध',
      developmentalFocusEn: `Shifting from anxious proving of personal worth to grounded execution of natural capabilities under ${ak} Atmakaraka wisdom.`,
      developmentalFocusHi: `दूसरों के सामने खुद को सिद्ध करने की चिंता से मुक्त होकर अपने स्वाभाविक कर्म को शांति से करना।`,
      classicalSignificance: 'Saturn–Jupiter maturation axis.'
    }
  ];

  // --------------------------------------------------------------------------
  // 6. DIMENSION 5.7 & 5.8: VAIRĀGYA & MOKṢA ORIENTATION
  // --------------------------------------------------------------------------
  const vairagyaThemes: VairagyaThemeResult = {
    relevance: [12, 8].includes(ketuPl?.house || 0) || [12, 8].includes(saturnPl?.house || 0) ? 'STRONG_THEME' : 'MODERATE_SIMPLIFICATION',
    explanationEn: `Your chart indicates an evolving appreciation for mental simplification, emotional decluttering, and freedom from excessive worldly attachment.`,
    explanationHi: `आपकी कुंडली मानसिक सादगी, अनावश्यक उलझनों से दूरी एवं आंतरिक स्वाधीनता के प्रति सहज आकर्षण दर्शाती है।`,
    protectiveClarificationEn: 'Vairagya in Vedic philosophy does NOT mean abandoning family or duties; it means acting with dedication while remaining free from obsessive anxiety over outcomes.',
    protectiveClarificationHi: 'वैदिक दर्शन में वैराग्य का अर्थ परिवार या कर्म छोड़ना कदापि नहीं है; इसका अर्थ है कर्म पूर्ण निष्ठा से करना किंतु परिणाम की चिंता से मुक्त रहना।',
    astrologicalMarkers: [`Ketu in House ${ketuPl?.house || 12}`, `12th Lord ${h12Lord} in House ${h12LordHouse}`]
  };

  const mokshaOrientation: MokshaOrientationResult = {
    relevanceEn: `Mokṣa-oriented themes (inner liberation, mental stillness, transcendence of petty conflicts) appear meaningful in your long-term developmental cycle.`,
    relevanceHi: `मोक्ष-उन्मुख विषय (आंतरिक स्वतंत्रता, मानसिक शांति एवं संकीर्ण विवादों से ऊपर उठना) आपकी दीर्घकालिक चेतना में सार्थक स्थान रखते हैं।`,
    prominence: rawConvergenceScore >= 52 ? 'MEANINGFUL_THEME' : 'BALANCED_AIM',
    classicalBhavasInvolved: [12, 9, 8, 4],
    karakamshaIndicatorEn: `Atmakaraka ${ak} situated in ${karakamsha.signEn} Navamsha (12th from it ruled by ${ishtaDevata.twelfthSignD9}) highlights subtle spiritual inquiry.`,
    karakamshaIndicatorHi: `नवांश में आत्मकारक (${ak}) की स्थिति आंतरिक आत्म-सत्य के अन्वेषण को रेखांकित करती है।`,
    protectiveNoteEn: 'VedangJyotisham strictly avoids dogmatic claims such as "You will attain Moksha"; liberation is an ongoing inward freedom cultivated through conscious living.',
    protectiveNoteHi: 'ज्योतिष किसी के मोक्ष प्राप्ति का अंतिम दावा नहीं करता; यह चेतना की स्वतंत्रता का सतत अभ्यास है।'
  };

  // --------------------------------------------------------------------------
  // 7. DIMENSION 5.9 to 5.12: DHARMA–MOKṢA BALANCE & INTEGRATION
  // Dynamically classified into 4 distinct classical models
  // --------------------------------------------------------------------------
  let dharmaMokshaPattern: DharmaMokshaPatternType = 'SPIRITUALITY_THROUGH_RESPONSIBILITY';
  let dharmaMokshaTitleEn = 'Spirituality Lived Through Worldly Responsibility';
  let dharmaMokshaTitleHi = 'सांसारिक उत्तरदायित्वों के माध्यम से आध्यात्मिक साधना';
  let dharmaMokshaSynthesisEn = 'In your chart, spiritual growth does not require withdrawal from life. Worldly obligations, family care, and professional ethics serve as the primary forge for spiritual maturity.';
  let dharmaMokshaSynthesisHi = 'आपकी कुंडली में आध्यात्मिक विकास जीवन से पलायन की मांग नहीं करता। पारिवारिक दायित्व, निष्पक्ष कर्म एवं सामाजिक कर्तव्य ही आत्म-शुद्धि का मुख्य साधन बनते हैं।';

  if ([12, 8].includes(ketuPl?.house || 0) && [12, 8].includes(saturnPl?.house || 0)) {
    dharmaMokshaPattern = 'PERIODS_OF_SIMPLIFICATION';
    dharmaMokshaTitleEn = 'Ascetic Simplicity & Detached Contemplation';
    dharmaMokshaTitleHi = 'आंतरिक सादगी एवं एकांत-साधना संतुलन';
    dharmaMokshaSynthesisEn = 'Your chart has strong Moksha indicators that require regular periods of quiet retreat, boundary setting, and mental decluttering to recharge your spirit.';
    dharmaMokshaSynthesisHi = 'आपकी कुंडली में मोक्ष भावों का विशेष प्रभाव है, जो मानसिक शांति बनाए रखने हेतु समय-समय पर एकांत एवं सादगी की मांग करता है।';
  } else if ([1, 9, 10].includes(sunPl?.house || 0) && [1, 9, 5].includes(jupiterPl?.house || 0)) {
    dharmaMokshaPattern = 'DYNAMIC_INTEGRATION_OF_AIMS';
    dharmaMokshaTitleEn = 'Dharmic Leadership & Ethical Stewardship';
    dharmaMokshaTitleHi = 'धार्मिक नेतृत्व एवं न्यायप्रिय कर्तव्य-पालन';
    dharmaMokshaSynthesisEn = 'Dharma (Duty) and Artha (Creation) act as the solid foundation in your chart. Ethical excellence and uplifting community are your highest spiritual expressions.';
    dharmaMokshaSynthesisHi = 'धर्म और अर्थ आपकी कुंडली के मजबूत आधार हैं। आजीविका में सत्यनिष्ठा एवं समाज को दिशा देना ही आपकी वास्तविक साधना है।';
  } else if (h8Occupants.length > 0 || [8, 12].includes(h8LordHouse)) {
    dharmaMokshaPattern = 'INNER_FREEDOM_AMIDST_ENTERPRISE';
    dharmaMokshaTitleEn = 'Inner Crucible: Transformation Through Life Cycles';
    dharmaMokshaTitleHi = 'जीवन-चक्रों के मंथन से चेतना का रूपांतरण';
    dharmaMokshaSynthesisEn = 'Your spiritual journey is forged through profound life transitions. Overcoming obstacles and navigating changes burns away illusions and awakens deep wisdom.';
    dharmaMokshaSynthesisHi = 'आपकी आध्यात्मिक यात्रा जीवन के परिवर्तनों और अनुभवों के मंथन से तपकर निकलती है, जो भ्रमों को मिटाकर अंतर्दृष्टि देती है।';
  }

  const dharmaMokshaRelationship: DharmaMokshaBalanceResult = {
    patternType: dharmaMokshaPattern,
    patternTitleEn: dharmaMokshaTitleEn,
    patternTitleHi: dharmaMokshaTitleHi,
    synthesisEn: dharmaMokshaSynthesisEn,
    synthesisHi: dharmaMokshaSynthesisHi,
    worldlyResponsibilityRoleEn: `Duty is your temple: meeting daily responsibilities truthfully is your highest spiritual discipline (Svadharma).`,
    worldlyResponsibilityRoleHi: `कर्तव्य ही साधना है: दैनिक दायित्वों को निष्पक्षता एवं सत्यनिष्ठा से निभाना ही सर्वोच्च अनुशासन है।`,
    fourPurusharthasBalanceEn: `Dharma (Ethics) guides Artha (Wealth), keeping Kama (Desire) refined and paving the way for Moksha (Freedom).`,
    fourPurusharthasBalanceHi: `धर्म द्वारा अर्थ का संचालन, जिससे काम मर्यादित रहता है और मोक्ष की चेतना जाग्रत होती है।`
  };

  const relationshipExpression: SpiritualityRelationshipResult = {
    insightEn: `Relationships serve as mirrors for self-awareness, teaching patience, compassionate boundaries, and selfless care.`,
    insightHi: `संबंध आत्म-जागरूकता का दर्पण बनते हैं, जो धैर्य, करुणा और निःस्वार्थ भाव की सीख देते हैं।`,
    familyRoleEn: `Providing dependable shelter and moral grounding for loved ones without imposing dogmatic beliefs.`,
    familyRoleHi: `परिजनों को बिना किसी हठ के संबल, प्रेम और नैतिक मार्गदर्शन देना।`,
    communityRoleEn: `Valuing depth of connection, ethical integrity, and mutual respect over superficial networking.`,
    communityRoleHi: `दिखावटी संबंधों के स्थान पर आत्मीय और सच्चे संपर्कों को प्राथमिकता देना।`
  };

  const careerExpression: SpiritualityCareerResult = {
    isCareerDirectlySpiritual: false,
    expressionModeEn: `Ethical competence and fairness in your chosen profession`,
    expressionModeHi: `अपनी आजीविका में नैतिक उत्कृष्टता एवं प्रामाणिकता`,
    vocationalChannelEn: `Spirituality expresses through fairness, professional competence, mentoring juniors, and refusing corrupt compromises.`,
    vocationalChannelHi: `आध्यात्मिकता का प्रकटीकरण निष्पक्षता, व्यावसायिक दक्षता, सहयोगियों के मार्गदर्शन और नीतिगत आचरण से होता है।`,
    clearDistinctionEn: `You do NOT need to abandon your worldly role, wear robes, or become an astrologer/guru to live spiritually; ordinary work performed with integrity is sacred.`,
    clearDistinctionHi: `आध्यात्मिक होने के लिए सन्यासी, गुरु या ज्योतिषी बनना आवश्यक नहीं है; ईमानदारी से किया गया कर्म ही पवित्र है।`
  };

  const materialLifeExpression: SpiritualityMaterialLifeResult = {
    integrationSynthesisEn: `Clean material wealth and spiritual freedom are complementary in Vedic philosophy (Lakshmi and Narayana harmony). Build prosperity ethically to support dependents and charity without greed.`,
    integrationSynthesisHi: `वैदिक दर्शन में न्यायोचित धन एवं आध्यात्मिक चेतना एक दूसरे के पूरक हैं (लक्ष्मी-नारायण समन्वय)। लोभ रहित होकर जीवन एवं सेवा हेतु समृद्धि का सदुपयोग करें।`,
    wealthAndSpiritualHarmonyEn: `Earn with righteousness (Dharma), spend with discretion and generosity, and remain internally detached from possession.`,
    wealthAndSpiritualHarmonyHi: `धर्मपूर्वक उपार्जन करें, सदुपयोग करें और मानसिक रूप से अपरिग्रह भाव रखें।`
  };

  // --------------------------------------------------------------------------
  // 8. DIMENSION 5.13 & 5.14: GURU, TRADITION & JÑĀNA
  // --------------------------------------------------------------------------
  const guruTraditionTheme: GuruTraditionResult = {
    guidanceImportanceEn: `Guidance from authentic preceptors, noble elders, and verified scriptures is deeply beneficial for clarifying blind spots, though blind worship is avoided.`,
    guidanceImportanceHi: `योग्य मार्गदर्शकों अथवा शास्त्रीय ज्ञान से संशय दूर होते हैं, किंतु अंधानुकरण से बचना आपके स्वभाव के अनुकूल है।`,
    traditionOrientationEn: `Respect for classical Vedic tradition verified through your own reason and lived experience.`,
    traditionOrientationHi: `परंपरा के प्रति आदर रखते हुए भी स्वयं के विवेक और अनुभव से सत्य की पुष्टि करना।`,
    bhratrikarakaJupiterIndicatorEn: `Bhratrikaraka (${bk}) in House ${bkHouse} and Jupiter in House ${jupiterPl?.house || 9} emphasize respect for noble mentors.`,
    bhratrikarakaJupiterIndicatorHi: `भ्रातृकारक (${bk}) एवं गुरु की स्थिति श्रेष्ठ मार्गदर्शकों के प्रति आदर भाव दर्शाती है।`,
    protectiveNoteEn: `VedangJyotisham does not predict specific cults, Ashrams, or sectarian gurus; your true guide is verified wisdom and inner conscience.`,
    protectiveNoteHi: `ज्योतिष किसी विशिष्ट आश्रम या व्यक्ति का नाम नहीं बताता; आपका वास्तविक गुरु शास्त्र-सम्मत विवेक और अंतरात्मा है।`
  };

  const jnanaTheme: JnanaThemeResult = {
    studyAndInquiryRoleEn: `Regular contemplation and study of philosophical wisdom cleanses mental confusion and maintains life balance.`,
    studyAndInquiryRoleHi: `नियमित स्वाध्याय और दार्शनिक चिंतन मानसिक भ्रम को दूर कर जीवन में संतुलन बनाए रखता है।`,
    scripturalRelevanceEn: `Studying classic Vedic, Upanishadic, and Gita shlokas provides durable cognitive anchor points during stressful life phases.`,
    scripturalRelevanceHi: `कठिन समय में गीता एवं उपनिषदों के विचार मन को अटूट धैर्य प्रदान करते हैं।`,
    contemplativeDiscernmentEn: `Cultivating witness consciousness (Sakshi Bhava) to observe changing life conditions without panic.`,
    contemplativeDiscernmentHi: `जीवन के उतार-चढ़ाव में भयभीत न होकर साक्षी भाव से परिस्थितियों को देखना।`
  };

  // --------------------------------------------------------------------------
  // 9. DIMENSION 5.16, 5.17: LIFE CHAPTERS & CURRENT CHRONOLOGICAL STATE
  // Dynamically aligned to native's real age and Vimshottari Mahadasha timeline
  // --------------------------------------------------------------------------
  const dashaList = kundali.dashas || [];
  const earlyDasha = dashaList[0]?.planet || 'Ketu';
  const midDasha = dashaList[Math.min(2, dashaList.length - 1)]?.planet || dashaAtFocus.maha;
  const lateDasha = dashaList[Math.min(4, dashaList.length - 1)]?.planet || 'Jupiter';

  const spiritualLifeChapters: SpiritualLifeChapter[] = [
    {
      chapterNumber: 1,
      chapterTitleEn: 'Inquiry & Moral Awakening',
      chapterTitleHi: 'जिज्ञासा एवं प्रारंभिक संस्कार',
      approxAgeRange: 'Youth (Age 15–28)',
      themeEn: `Initial questioning of worldly conventions, seeking ideals, and absorbing foundational values under ${earlyDasha} Dasha influences.`,
      themeHi: `संसार के नियमों पर प्रश्न, आदर्शों की खोज एवं ${earlyDasha} दशा प्रभाव में संस्कारों का निर्माण।`,
      dashaCatalyst: `${earlyDasha} Dasha Era`,
      isCurrentChapter: nativeAge <= 28
    },
    {
      chapterNumber: 2,
      chapterTitleEn: 'Testing Svadharma in Worldly Crucible',
      chapterTitleHi: 'सांसारिक संघर्षों में स्वधर्म की कसौटी',
      approxAgeRange: 'Prime Life (Age 29–48)',
      themeEn: `Balancing career ambition, family obligations, and the struggle to maintain inner calm under active ${dashaAtFocus.maha} Mahadasha.`,
      themeHi: `आजीविका, पारिवारिक उत्तरदायित्व एवं मानसिक शांति के मध्य संतुलन साधने का प्रयास (${dashaAtFocus.maha} महादशा)।`,
      dashaCatalyst: `${dashaAtFocus.maha} Mahadasha cycle`,
      isCurrentChapter: nativeAge > 28 && nativeAge <= 48
    },
    {
      chapterNumber: 3,
      chapterTitleEn: 'Inner Deepening & Dispassionate Clarity',
      chapterTitleHi: 'गंभीर अंतर्मुखता एवं निर्मल विवेक',
      approxAgeRange: 'Maturity (Age 49–65)',
      themeEn: `Gradual reduction of trivial anxieties, deepening meditative stillness, and prioritizing lasting spiritual legacy under ${midDasha} cycle.`,
      themeHi: `व्यर्थ चिंताओं से मुक्ति, ध्यान में गहराई और वास्तविक जीवन-सार पर ध्यान (${midDasha} चक्र)।`,
      dashaCatalyst: `${midDasha} cycle`,
      isCurrentChapter: nativeAge > 48 && nativeAge <= 65
    },
    {
      chapterNumber: 4,
      chapterTitleEn: 'Serene Detachment & Transcendent Transmission',
      chapterTitleHi: 'शांत अनासक्ति एवं ज्ञान-दान',
      approxAgeRange: 'Elderhood (Age 65+)',
      themeEn: `Contentment, release of personal claims, quiet blessing to younger generations, and peaceful inner contemplation under ${lateDasha} horizon.`,
      themeHi: `संतोष, निजी दावों का विसर्जन, भावी पीढ़ी को आशीर्वाद एवं शांत आत्म-लीनता (${lateDasha} क्षितिज)।`,
      dashaCatalyst: `${lateDasha} culmination`,
      isCurrentChapter: nativeAge > 65
    }
  ];

  // Current State strictly anchored to Focus Date & real Mahadasha / Antardasha
  const isDashaSpiritual = ['Jupiter', 'Ketu', 'Sun', 'Saturn'].includes(dashaAtFocus.maha) ||
    ['Jupiter', 'Ketu', 'Sun', 'Saturn'].includes(dashaAtFocus.antar);

  const getDashaSpiritualRoleEn = (maha: PlanetName, antar: PlanetName) => {
    return `The active ${maha} Mahadasha with ${antar} Antardasha activates a specific curriculum: cultivating ${maha === 'Jupiter' ? 'higher wisdom and moral clarity' : maha === 'Saturn' ? 'endurance, duty-bound patience, and simplification' : maha === 'Ketu' ? 'deep inward detachment and meditative focus' : maha === 'Mercury' ? 'scriptural discernment and honest communication' : maha === 'Venus' ? 'sacred gratitude, relationship harmony, and devotion' : maha === 'Sun' ? 'soul integrity and moral courage' : maha === 'Moon' ? 'mental peace and emotional balance' : maha === 'Mars' ? 'tapasya, vital discipline, and overcoming inertia' : 'seeing through illusions and building authentic values'}.`;
  };

  const getDashaSpiritualRoleHi = (maha: PlanetName, antar: PlanetName) => {
    return `सक्रिय ${maha} महादशा एवं ${antar} अंतर्दशा का कालखंड आपके लिए विशेष रूप से ${maha === 'Jupiter' ? 'सद्विवेक, ज्ञान एवं गुरु कृपा' : maha === 'Saturn' ? 'धैर्य, कर्तव्य-पालन एवं वैराग्य' : maha === 'Ketu' ? 'अंतर्मुखी ध्यान एवं विरक्ति' : maha === 'Mercury' ? 'स्वाध्याय एवं विचार-शुद्धि' : maha === 'Venus' ? 'समर्पण एवं भक्ति-भाव' : maha === 'Sun' ? 'आत्म-बोध एवं तेजस्विता' : maha === 'Moon' ? 'मानसिक शांति व करुणा' : maha === 'Mars' ? 'तपस्या व अनुशासन' : 'सत्य-अन्वेषण'} को जाग्रत कर रहा है।`;
  };

  // Authoritative Panchanga calculation for Focus Date
  const coordinates: PanchangaCoordinates = {
    latitude: options?.coordinates?.latitude ?? (kundali.birthDetails?.latitude || 28.6139),
    longitude: options?.coordinates?.longitude ?? (kundali.birthDetails?.longitude || 77.2090),
    timezoneOffsetMinutes: options?.coordinates?.timezoneOffsetMinutes ?? (kundali.birthDetails?.timezoneOffsetMinutes ?? 330)
  };

  const focusPanchanga = calculateAuthoritativePanchanga(effectiveFocusDate, coordinates);

  // Active Observance & Parva extraction
  const activeObservances: string[] = [];
  const activeObservancesHi: string[] = [];

  if (focusPanchanga.vratObservance.hasVrat && focusPanchanga.vratObservance.vratName) {
    activeObservances.push(focusPanchanga.vratObservance.vratName);
    activeObservancesHi.push(focusPanchanga.vratObservance.vratNameHi);
  }

  if (focusPanchanga.sankranti.isSankrantiDay) {
    const sDesc = `${focusPanchanga.sankranti.sankrantiNameEn} (${focusPanchanga.sankranti.formattedPunyaKaal || 'Punya Kaal'})`;
    const sDescHi = `${focusPanchanga.sankranti.sankrantiNameHi} (${focusPanchanga.sankranti.formattedPunyaKaal || 'पुण्य काल'})`;
    activeObservances.push(sDesc);
    activeObservancesHi.push(sDescHi);
  }

  if (focusPanchanga.pitruPaksha.isActive) {
    const pDesc = `Pitru Paksha: ${focusPanchanga.pitruPaksha.shraddhaTithiNameEn || 'Shraddha Tithi'}`;
    const pDescHi = `पितृ पक्ष: ${focusPanchanga.pitruPaksha.shraddhaTithiNameHi || 'श्राद्ध तिथि'}`;
    activeObservances.push(pDesc);
    activeObservancesHi.push(pDescHi);
  }

  const alignmentPartsEn: string[] = [];
  const alignmentPartsHi: string[] = [];

  if (focusPanchanga.sankranti.isSankrantiDay) {
    alignmentPartsEn.push(`Surya Sankranti (${focusPanchanga.sankranti.sankrantiNameEn}): solar ingress into ${focusPanchanga.sankranti.toSignEn}. Punya Kaal (${focusPanchanga.sankranti.formattedPunyaKaal || 'solar transition'}) offers high merit for Gayatri japa, charity (Dāna), and Surya Arghya.`);
    alignmentPartsHi.push(`सूर्य संक्रांति (${focusPanchanga.sankranti.sankrantiNameHi}): ${focusPanchanga.sankranti.toSignHi} राशि में सूर्य का प्रवेश। पुण्य काल (${focusPanchanga.sankranti.formattedPunyaKaal || 'संक्रांति काल'}) में सूर्य अर्घ्य, दान एवं गायत्री जप विशेष फलदायी है।`);
  }

  if (focusPanchanga.vratObservance.hasVrat && focusPanchanga.vratObservance.vratCategory === 'EKADASHI') {
    alignmentPartsEn.push(`Aligned with ${focusPanchanga.vratObservance.vratName}: an auspicious window for Sattvic diet, mindfulness, and Japa (${ishtaDevata.mantraEn}).`);
    alignmentPartsHi.push(`आज ${focusPanchanga.vratObservance.vratNameHi} का पावन दिवस है: सात्विक आहार/उपवास, अंतर्मुखी ध्यान एवं ${ishtaDevata.mantraHi} के जप हेतु अत्यंत अनुकूल।`);
  } else if (focusPanchanga.vratObservance.hasVrat && focusPanchanga.vratObservance.vratCategory === 'PRADOSHA') {
    alignmentPartsEn.push(`Aligned with ${focusPanchanga.vratObservance.vratName} (Pradosha): twilight contemplation, Shiva remembrance, and releasing mental restlessness.`);
    alignmentPartsHi.push(`आज ${focusPanchanga.vratObservance.vratNameHi} (प्रदोष) का शुभ योग है: सायंकाल ध्यान, शिव स्मरण एवं मानसिक शांति हेतु विशेष अनुकूल।`);
  } else if (focusPanchanga.vratObservance.hasVrat && focusPanchanga.vratObservance.vratCategory === 'SHIVARATRI') {
    alignmentPartsEn.push(`Aligned with ${focusPanchanga.vratObservance.vratName}: auspicious opportunity for inward stillness, mantra sadhana, and overcoming inertia.`);
    alignmentPartsHi.push(`आज ${focusPanchanga.vratObservance.vratNameHi} का अवसर है: अंतर्मुखी मौन, मंत्र जप एवं आत्म-शुद्धि हेतु विशेष फलदायी।`);
  } else if (focusPanchanga.vratObservance.hasVrat && focusPanchanga.vratObservance.vratCategory === 'PURNIMA') {
    alignmentPartsEn.push(`Aligned with ${focusPanchanga.vratObservance.vratName} (Full Moon): ideal for sacred contemplation, gratitude, and cultivating mental clarity.`);
    alignmentPartsHi.push(`आज ${focusPanchanga.vratObservance.vratNameHi} (पूर्णिमा) का पावन दिन है: कृतज्ञता, सत्यनारायण कथा एवं मन की निर्मलता हेतु श्रेष्ठ।`);
  } else if (focusPanchanga.pitruPaksha.isActive) {
    alignmentPartsEn.push(`Pitru Paksha (${focusPanchanga.pitruPaksha.shraddhaTithiNameEn || 'Shraddha'}): sacred window for ancestral gratitude (Tarpana/Shraddha), contemplation on impermanence, and compassionate giving.`);
    alignmentPartsHi.push(`पितृ पक्ष (${focusPanchanga.pitruPaksha.shraddhaTithiNameHi || 'श्राद्ध'}): पूर्वजों के प्रति कृतज्ञता (तर्पण/श्राद्ध), अनित्यता का बोध एवं परोपकार हेतु पावन कालखंड।`);
  }

  if (alignmentPartsEn.length === 0) {
    alignmentPartsEn.push(`Panchanga aligns with ${focusPanchanga.tithi.name} (${focusPanchanga.paksha}) in ${focusPanchanga.nakshatra.name} Nakshatra: consistent daily sadhana (${sadhanaThemes[0]?.nameEn || 'Sādhana'}) is favored.`);
    alignmentPartsHi.push(`पंचांग अनुसार आज ${focusPanchanga.tithi.name} (${focusPanchanga.paksha === 'Shukla Paksha' ? 'शुक्ल पक्ष' : 'कृष्ण पक्ष'}), ${focusPanchanga.nakshatra.name} नक्षत्र है: नियमित रूप से ${sadhanaThemes[0]?.nameHi || 'दैनिक साधना'} करना श्रेयस्कर है।`);
  }

  const dailySadhanaAlignmentEn = alignmentPartsEn.join(' ');
  const dailySadhanaAlignmentHi = alignmentPartsHi.join(' ');

  const spiritualPanchangaContext: SpiritualPanchangaContext = {
    tithiName: focusPanchanga.tithi.name,
    tithiPaksha: focusPanchanga.paksha,
    nakshatraName: focusPanchanga.nakshatra.name,
    yogaName: focusPanchanga.yoga.name,
    samvatSummary: focusPanchanga.samvatString,
    activeObservances,
    activeObservancesHi,
    isSankranti: Boolean(focusPanchanga.sankranti.isSankrantiDay),
    sankrantiNameEn: focusPanchanga.sankranti.isSankrantiDay ? focusPanchanga.sankranti.sankrantiNameEn : undefined,
    sankrantiNameHi: focusPanchanga.sankranti.isSankrantiDay ? focusPanchanga.sankranti.sankrantiNameHi : undefined,
    punyaKaalWindow: focusPanchanga.sankranti.isSankrantiDay ? focusPanchanga.sankranti.formattedPunyaKaal : undefined,
    isPitruPaksha: Boolean(focusPanchanga.pitruPaksha.isActive),
    shraddhaTithiNameEn: focusPanchanga.pitruPaksha.isActive ? focusPanchanga.pitruPaksha.shraddhaTithiNameEn : undefined,
    shraddhaTithiNameHi: focusPanchanga.pitruPaksha.isActive ? focusPanchanga.pitruPaksha.shraddhaTithiNameHi : undefined,
    dailySadhanaAlignmentEn,
    dailySadhanaAlignmentHi
  };

  const currentSpiritualState: CurrentSpiritualStateResult = {
    focusDate: focusDateStr,
    activeStatus: 'ACTIVE_NOW',
    currentThemeEn: isDashaSpiritual
      ? `Active ${dashaAtFocus.maha}–${dashaAtFocus.antar} Spiritual Window`
      : `Subtle Dharmic Cultivation Under ${dashaAtFocus.maha}–${dashaAtFocus.antar}`,
    currentThemeHi: isDashaSpiritual
      ? `वर्तमान ${dashaAtFocus.maha}–${dashaAtFocus.antar} दशा अंतर्गत आध्यात्मिक जाग्रति`
      : `${dashaAtFocus.maha}–${dashaAtFocus.antar} दशा अंतर्गत शांत धर्म-साधना`,
    activeDashaPeriod: `${dashaAtFocus.maha} MD – ${dashaAtFocus.antar} AD`,
    dashaSpiritualRoleEn: getDashaSpiritualRoleEn(dashaAtFocus.maha, dashaAtFocus.antar),
    dashaSpiritualRoleHi: getDashaSpiritualRoleHi(dashaAtFocus.maha, dashaAtFocus.antar),
    gocharTriggerSummaryEn: `Jupiter transiting House ${jupiterTransitHouseLagna} (from Lagna) and Saturn transiting House ${saturnTransitHouseLagna} establish a grounded cosmic environment for consistent daily sadhana.`,
    gocharTriggerSummaryHi: `लग्न से ${jupiterTransitHouseLagna}वें भाव में गुरु एवं ${saturnTransitHouseLagna}वें भाव में शनि का गोचर नियमित साधना एवं मानसिक धैर्य हेतु अनुकूल आधार प्रदान कर रहा है।`,
    recommendedFocusEn: activeObservances.length > 0
      ? `${dailySadhanaAlignmentEn} Practice ${sadhanaThemes[0]?.nameEn || 'daily contemplation'}, chant ${ishtaDevata.mantraEn}, and maintain calm awareness.`
      : `Prioritize ${sadhanaThemes[0]?.nameEn || 'daily contemplation'}, chant ${ishtaDevata.mantraEn}, and cultivate patient witness consciousness.`,
    recommendedFocusHi: activeObservancesHi.length > 0
      ? `${dailySadhanaAlignmentHi} ${sadhanaThemes[0]?.nameHi || 'दैनिक साधना'} को प्राथमिकता दें, ${ishtaDevata.mantraHi} का जप करें एवं साक्षी भाव रखें।`
      : `${sadhanaThemes[0]?.nameHi || 'दैनिक साधना'} को प्राथमिकता दें, ${ishtaDevata.mantraHi} का जप करें एवं साक्षी भाव रखें।`,
    windowStartDate: dashaAtFocus.startDate,
    windowEndDate: dashaAtFocus.endDate,
    peakDate: (!isNaN(new Date(dashaAtFocus.startDate).getTime()) && !isNaN(new Date(dashaAtFocus.endDate).getTime()))
      ? new Date(Math.round((new Date(dashaAtFocus.startDate).getTime() + new Date(dashaAtFocus.endDate).getTime()) / 2)).toISOString().split('T')[0]
      : dashaAtFocus.startDate,
    panchangaContext: spiritualPanchangaContext
  };

  const dashaWavePeak = (!isNaN(new Date(dashaAtFocus.startDate).getTime()) && !isNaN(new Date(dashaAtFocus.endDate).getTime()))
    ? new Date(Math.round((new Date(dashaAtFocus.startDate).getTime() + new Date(dashaAtFocus.endDate).getTime()) / 2)).toISOString().split('T')[0]
    : dashaAtFocus.startDate;

  const jupUpcomingStart = `${effectiveFocusDate.getFullYear() + 1}-05-01`;
  const jupUpcomingEnd = `${effectiveFocusDate.getFullYear() + 2}-06-15`;
  const jupUpcomingPeak = new Date(Math.round((new Date(jupUpcomingStart).getTime() + new Date(jupUpcomingEnd).getTime()) / 2)).toISOString().split('T')[0];

  const activationWindows: SpiritualActivationWindow[] = [
    {
      windowTitleEn: `${dashaAtFocus.maha}–${dashaAtFocus.antar} Active Spiritual Wave`,
      windowTitleHi: `${dashaAtFocus.maha}–${dashaAtFocus.antar} दशा अंतर्गत सक्रिय साधना काल`,
      startDate: dashaAtFocus.startDate,
      endDate: dashaAtFocus.endDate,
      peakDate: dashaWavePeak,
      status: 'ACTIVE_NOW',
      dashaRuler: dashaAtFocus.maha,
      catalystDescriptionEn: `An active temporal corridor favoring deep study, inner restructuring, and emotional maturity.`,
      catalystDescriptionHi: 'गहन स्वाध्याय, आंतरिक पुनर्निर्माण एवं मानसिक परिपक्वता हेतु महत्वपूर्ण समय।',
      resonanceScore: isDashaSpiritual ? 88 : 72
    },
    {
      windowTitleEn: `Upcoming Jupiter Transit in ${transitRef.jupiterSign === 'Taurus' ? 'Gemini' : 'Cancer'}`,
      windowTitleHi: `आगामी देवगुरु गोचर संमिलन`,
      startDate: jupUpcomingStart,
      endDate: jupUpcomingEnd,
      peakDate: jupUpcomingPeak,
      status: 'UPCOMING',
      dashaRuler: 'Jupiter',
      catalystDescriptionEn: 'Upcoming planetary alignment stimulating pilgrimage, visiting sacred sanctuaries, and deeper philosophical study.',
      catalystDescriptionHi: 'तीर्थाटन, शांत रमणीय स्थलों की यात्रा एवं गहन दार्शनिक अध्ययन का आगामी सुअवसर।',
      resonanceScore: 78
    }
  ];

  // --------------------------------------------------------------------------
  // 10. DIMENSION 5.19 & 5.20: DYNAMIC CHALLENGES & INTEGRATION
  // Detects chart-specific afflictions (Guru Chandal, Saturn aspect, Mars friction, etc.)
  // --------------------------------------------------------------------------
  const challenges: SpiritualChallenge[] = [];

  // Check 1: Rahu-Jupiter / 9th house Rahu (Restless Skepticism)
  if (rahuPl && (rahuPl.house === 9 || rahuPl.house === 5 || (jupiterPl && rahuPl.house === jupiterPl.house))) {
    challenges.push({
      challengeTitleEn: 'Intellectual Skepticism & Restless Searching',
      challengeTitleHi: 'अति-संशय एवं चंचल अन्वेषण',
      growthOpportunityEn: 'Notice tendencies to jump between different traditions or teachers without grounding deeply in one practice for at least a year.',
      growthOpportunityHi: 'विभिन्न परंपराओं या विचारों में भटकने के स्थान पर किसी एक पद्धति पर निष्ठा से टिकने का अभ्यास करें।',
      shastricPrecaution: 'Patanjali Yoga Sutra 1.14: "Sa tu dirghakala nairantarya satkara asevito dridhabhumih" (Practice becomes firmly grounded when cultivated consistently over a long time).'
    });
  }

  // Check 2: Saturn aspect on Moon / Lagna or Saturn in 12th/1st (Despondency & Guilt)
  if (saturnPl && ([1, 12, 8].includes(saturnPl.house) || (moonPl && Math.abs(saturnPl.house - moonPl.house) === 7))) {
    challenges.push({
      challengeTitleEn: 'Spiritual Melancholy & Excessive Self-Judgment',
      challengeTitleHi: 'अति-गंभीरता, निराशा एवं आत्म-आलोचना',
      growthOpportunityEn: 'Avoid self-punishing standards or mistaking emotional heaviness for authentic spiritual detachment. Cultivate gentle self-compassion.',
      growthOpportunityHi: 'साधना में अनावश्यक उदासी या स्वयं को दोषी ठहराने से बचें; ईश्वर की कृपा और सहज प्रसन्नता को स्थान दें।',
      shastricPrecaution: 'Bhagavad Gita 6.5: "Uddhared atmanatmanam" (Elevate yourself by yourself; do not degrade yourself).'
    });
  }

  // Check 3: Mercury / Sun prominence (Over-intellectualizing the sacred)
  if (mercuryPl && [1, 5, 9].includes(mercuryPl.house)) {
    challenges.push({
      challengeTitleEn: 'Over-Intellectualizing the Sacred',
      challengeTitleHi: 'साधना का अत्यधिक बौद्धिकीकरण',
      growthOpportunityEn: 'Ensure that reading, debating, and collecting theories do not substitute for actual silent meditation and practical kindness.',
      growthOpportunityHi: 'यह ध्यान रखें कि केवल पढ़ना और चर्चा करना वास्तविक मौन और सेवा का स्थान न ले ले।',
      shastricPrecaution: 'Viveka must lead to shanti (peace), not egoistic debate.'
    });
  }

  // Check 4: Mars prominence (Impatience with worldly friction)
  if (challenges.length < 2 && marsPl && [1, 8, 10, 12].includes(marsPl.house)) {
    challenges.push({
      challengeTitleEn: 'Impatience with Worldly Friction & Slow Progress',
      challengeTitleHi: 'सांसारिक परिस्थितियों के प्रति अधीरता',
      growthOpportunityEn: 'View daily obstacles and difficult personalities as raw curriculum for spiritual patience rather than interruptions to it.',
      growthOpportunityHi: 'दैनिक कठिनाइयों को साधना में विघ्न न मानकर धैर्य और समत्व की कसौटी समझें।',
      shastricPrecaution: 'Bhagavad Gita 2.48: "Samatvam Yoga Uchyate" (Evenness of mind is yoga).'
    });
  }

  // Fallback to maintain at least 2 challenges
  if (challenges.length < 2) {
    challenges.push({
      challengeTitleEn: 'Balancing Daily Duties with Contemplative Solitude',
      challengeTitleHi: 'दैनिक कर्तव्यों एवं एकांत साधना का संतुलन',
      growthOpportunityEn: 'Crafting non-negotiable 15-minute pockets of morning silence without feeling guilty about worldly obligations.',
      growthOpportunityHi: 'बिना किसी संकोच के प्रतिदिन १५ मिनट का मौन समय निकालें ताकि दिनभर चेतना स्थिर रहे।',
      shastricPrecaution: 'Duty performed with calm awareness is the highest sadhana.'
    });
  }

  const integrationThemes: SpiritualIntegrationResult = {
    holisticPathSummaryEn: `True spirituality in your chart is an integrated way of being: clear in mind (${getCleanLabelEn(p1)}), devoted in heart (${ishtaDevata.deityEn}), steady in action (Svadharma), and peaceful in spirit (Mokṣa).`,
    holisticPathSummaryHi: `आपकी कुंडली में सच्ची आध्यात्मिकता जीवन का समग्र रूप है: विचार में विवेक (${getCleanLabelHi(p1)}), हृदय में श्रद्धा (${ishtaDevata.deityHi}), कर्म में निष्काम कर्तव्य और अंतःकरण में शांति (मोक्ष)।`,
    actionableDailyPrinciplesEn: [
      `Begin each morning with 5–10 minutes of silent gratitude and ${ishtaDevata.mantraEn}.`,
      `Treat professional and family obligations as your sacred field of duty (Svadharma).`,
      `Set aside 15 minutes weekly for uplifting philosophical study (${sadhanaThemes[1]?.nameEn || 'Svādhyāya'}).`,
      `Practice intentional simplification: releasing minor ego disputes and unnecessary mental clutter.`
    ],
    actionableDailyPrinciplesHi: [
      `प्रत्येक प्रातः ५-१० मिनट कृतज्ञता एवं ${ishtaDevata.mantraHi} से दिन का आरंभ करें।`,
      `अपनी आजीविका एवं पारिवारिक कर्तव्यों को पूर्ण निष्ठा से ईश्वर-समर्पित कर्म मानें।`,
      `सप्ताह में कुछ समय उत्तम दार्शनिक साहित्य के अध्ययन हेतु निकालें।`,
      `छोटी-मोटी तकरार और अनावश्यक संग्रह को छोड़कर सादगी का अभ्यास करें।`
    ]
  };

  // Contradictions check
  const contradictions: SpiritualityContradictionItem[] = [];
  if (h10Occupants.length > 0 && [12, 8].includes(ketuPl?.house || 0)) {
    contradictions.push({
      contendingFactors: [`Strong 10th House (${h10Rasi}) Karma`, `Moksha-oriented Ketu in House ${ketuPl?.house}`],
      competingHypothesisEn: 'High worldly career activity competing with inward meditative withdrawal impulses.',
      competingHypothesisHi: 'उच्च व्यावसायिक सक्रियता का अंतर्मुखी वैराग्य के साथ संतुलन।',
      resolutionEn: 'Resolved through Karma Yoga: spiritualizing active career through integrity rather than abandoning worldly responsibilities.',
      resolutionHi: 'कर्म योग द्वारा समाधान: संन्यास के स्थान पर कर्म को ही साधना बनाकर चलना।'
    });
  }

  const alternatives: SpiritualityAlternativeItem[] = [
    {
      hypothesisEn: `Philosophical & Scholarly Pursuit of Truth (${getCleanLabelEn(p1)} Dominant)`,
      hypothesisHi: `दार्शनिक एवं स्वाध्याय प्रधान सत्य-शोध (${getCleanLabelHi(p1)})`,
      probabilityLevel: 'PRIMARY_RESONANCE',
      distinguishingFactors: [`${ak} Atmakaraka in House ${akHouse}`, `Jupiter in House ${jupiterPl?.house || 9}`, 'Intellectual discernment']
    },
    {
      hypothesisEn: `Devotional & Contemplative Path (${getCleanLabelEn(p2)} Complement)`,
      hypothesisHi: `भावपूर्ण एवं अंतर्मुखी साधना (${getCleanLabelHi(p2)})`,
      probabilityLevel: 'STRONG_ALTERNATIVE',
      distinguishingFactors: [`12th from Karakamsha ruled by ${ishtaDevata.twelfthSignD9}`, `Ketu in House ${ketuPl?.house || 12}`]
    }
  ];

  // --------------------------------------------------------------------------
  // ASSEMBLE COMPLETE EVIDENCE OBJECT
  // --------------------------------------------------------------------------
  const evidenceObject: SpiritualityEvidenceObject = {
    spiritualityInclination: {
      classification: inclinationClass,
      stability: 'INNATE_ENDURING',
      headlineEn: inclinationHeadlineEn,
      headlineHi: inclinationHeadlineHi,
      summaryEn: inclinationSummaryEn,
      summaryHi: inclinationSummaryHi,
      prominenceRank: rawConvergenceScore >= 75 ? 1 : rawConvergenceScore >= 50 ? 2 : 3,
      innateVsDevelopedEn,
      innateVsDevelopedHi,
      supportingEvidenceCount: evidenceItems.length
    },
    orientations: allOrientationItems,
    primaryOrientation: primaryOrientType,
    secondaryOrientations: secondaryOrientTypes,
    spiritualPath,
    sadhanaThemes,
    spiritualExperienceThemes,
    transformationThemes,
    vairagyaThemes,
    mokshaOrientation,
    dharmaMokshaRelationship,
    relationshipExpression,
    careerExpression,
    materialLifeExpression,
    guruTraditionTheme,
    jnanaTheme,
    developmentalStage,
    spiritualLifeChapters,
    currentSpiritualState,
    activationWindows,
    challenges,
    integrationThemes,
    evidence: evidenceItems,
    contradictions,
    alternatives,
    btrSensitivity: 'STABLE_ACROSS_BTR',
    stability: 'STABLE_NATAL',
    methodology: 'CLASSICAL_VERIFIED',
    calculatedAt: new Date().toISOString(),
    calculationVersion: 'SIME-V1.0-PARASHARI-JAIMINI'
  };

  // --------------------------------------------------------------------------
  // ASSEMBLE 8 CONCISE SEEKER CARDS (100% Dynamic, Chart-Specific)
  // --------------------------------------------------------------------------
  const seeker: SeekerSpiritualityPresentation = {
    // Card 1: Nature
    card1_spiritualNature: {
      cardId: 'card1_spiritualNature',
      titleEn: '1. Your Spiritual Nature',
      titleHi: '१. आपका आध्यात्मिक स्वभाव',
      sanskritTerm: 'आत्म-स्वभाव (Ātma-Svabhāva)',
      headlineEn: `${inclinationHeadlineEn} (${lagnaElement} Element)`,
      headlineHi: `${inclinationHeadlineHi} (${lagnaElement === 'FIRE' ? 'अग्नि तत्व' : lagnaElement === 'WATER' ? 'जल तत्व' : lagnaElement === 'AIR' ? 'वायु तत्व' : 'पृथ्वी तत्व'})`,
      bodyEn: `${inclinationSummaryEn} Your soul's foundational blueprint is shaped by ${lagnaRasi} Lagna, with Atmakaraka ${ak} situated in House ${akHouse} (${akSign}), guiding your inner temperament toward authentic truth.`,
      bodyHi: `${inclinationSummaryHi} आपका आत्म-स्वभाव ${lagnaRasi} लग्न एवं आत्मकारक ${ak} (${akHouse}वें भाव, ${akSign}) के प्रभाव से निर्धारित होता है, जो अंतःकरण को सत्य की ओर प्रेरित करता है।`,
      bulletPointsEn: [
        `Soul Significator: Atmakaraka ${ak} in ${akSign} (House ${akHouse})`,
        `Temperament: ${innateVsDevelopedEn}`,
        `Elemental Core: ${lagnaElement === 'FIRE' ? 'Dynamic truth-seeking and moral radiance' : lagnaElement === 'WATER' ? 'Intuitive depth, empathy, and mystic devotion' : lagnaElement === 'AIR' ? 'Philosophical discernment and reflective inquiry' : 'Disciplined duty, patient endurance, and grounded service'}.`
      ],
      bulletPointsHi: [
        `आत्मकारक ग्रह: ${ak} (${akHouse}वें भाव, ${akSign} राशि)`,
        `संस्कार: ${innateVsDevelopedHi}`,
        `तत्व चेतना: ${lagnaElement === 'FIRE' ? 'सत्य-अन्वेषी एवं तेजस्वी धर्म-साधना' : lagnaElement === 'WATER' ? 'गहन अंतर्दृष्टि, करुणा एवं भक्ति-संवेदना' : lagnaElement === 'AIR' ? 'दार्शनिक विचार, विवेक एवं स्वाध्याय' : 'धैर्यवान कर्म, निष्काम सेवा एवं स्थिरता'}।`
      ],
      evidenceSummaryEn: `${evidenceItems.length} verified classical factors converge across Dharma & Moksha houses.`,
      evidenceSummaryHi: `धर्म एवं मोक्ष भावों में ${evidenceItems.length} शास्त्रीय ग्रह योगों का संमिलन।`
    },

    // Card 2: Path
    card2_spiritualPath: {
      cardId: 'card2_spiritualPath',
      titleEn: '2. Your Spiritual Path',
      titleHi: '२. आपका आध्यात्मिक मार्ग',
      sanskritTerm: 'आध्यात्मिक पथ (Sādhana Mārga)',
      headlineEn: pathTitleEn,
      headlineHi: pathTitleHi,
      bodyEn: pathSynthesisEn,
      bodyHi: pathSynthesisHi,
      bulletPointsEn: [
        `Primary Current: ${p1.nameEn}`,
        `Complementary Current: ${p2.nameEn}`,
        `Path Essence: Harmonizing ${getCleanLabelEn(p1)} with practical life duties.`
      ],
      bulletPointsHi: [
        `मुख्य धारा: ${p1.nameHi}`,
        `सहायक धारा: ${p2.nameHi}`,
        `समन्वय: ${getCleanLabelHi(p1)} और दैनिक जीवन का संतुलित संगम।`
      ],
      evidenceSummaryEn: `Synthesized from primary ${getCleanLabelEn(p1)} (${p1.convergenceScore}%) and secondary ${getCleanLabelEn(p2)} (${p2.convergenceScore}%) alignments.`,
      evidenceSummaryHi: `${getCleanLabelHi(p1)} (${p1.convergenceScore}%) एवं ${getCleanLabelHi(p2)} (${p2.convergenceScore}%) के शास्त्रीय प्रमाणों से निष्कर्षित।`
    },

    // Card 3: Practice
    card3_spiritualPractice: {
      cardId: 'card3_spiritualPractice',
      titleEn: '3. Your Spiritual Practice',
      titleHi: '३. अनुकूल साधना व अभ्यास',
      sanskritTerm: 'सद्-अभ्यास (Sādhanā)',
      headlineEn: `${sadhanaThemes[0]?.nameEn.split('(')[0].trim()} & ${sadhanaThemes[1]?.nameEn.split('(')[0].trim()}`,
      headlineHi: `${sadhanaThemes[0]?.nameHi.split('(')[0].trim()} एवं ${sadhanaThemes[1]?.nameHi.split('(')[0].trim()}`,
      bodyEn: `Your optimal spiritual practice centers on ${ishtaDevata.deityEn}, guided by the 12th house from Karakamsha in Navamsha (${ishtaDevata.twelfthSignD9}) and 5th house of Purva Punya. Unforced, steady rhythm brings deep mental peace.`,
      bodyHi: `आपकी अनुकूल साधना नवांश कारकांश से १२वें भाव (${ishtaDevata.twelfthSignD9}) एवं पंचम भाव के अनुसार ${ishtaDevata.deityHi} की उपासना पर आधारित है। नियमित व सहज अभ्यास गहन शांति देता है।`,
      bulletPointsEn: [
        `Personal Ishta Devata: ${ishtaDevata.deityEn} (${ishtaDevata.mantraEn})`,
        `Primary Practice: ${sadhanaThemes[0]?.guidanceEn}`,
        `Secondary Practice: ${sadhanaThemes[1]?.guidanceEn}`,
        `Sacred Timing: ${sadhanaThemes[0]?.practicalTimingGuidanceEn}`
      ],
      bulletPointsHi: [
        `इष्ट देव / उपासना: ${ishtaDevata.deityHi} (${ishtaDevata.mantraHi})`,
        `मुख्य साधना: ${sadhanaThemes[0]?.guidanceHi}`,
        `सहायक साधना: ${sadhanaThemes[1]?.guidanceHi}`,
        `शुभ समय: ${sadhanaThemes[0]?.practicalTimingGuidanceHi}`
      ],
      evidenceSummaryEn: `Governed by 12th from Karakamsha (${ishtaDevata.twelfthSignD9}) and ${ak} Atmakaraka disposition.`,
      evidenceSummaryHi: `कारकांश से १२वें भाव (${ishtaDevata.twelfthSignD9}) एवं ${ak} आत्मकारक की शास्त्रीय स्थिति पर आधारित।`
    },

    // Card 4: Inner Development
    card4_innerDevelopment: {
      cardId: 'card4_innerDevelopment',
      titleEn: '4. Your Inner Development',
      titleHi: '४. आंतरिक चेतना का विकास',
      sanskritTerm: 'चेतना-विकास (Antar-Vikāsa)',
      headlineEn: `${developmentalStage.stageNameEn} (${ak} Soul Lesson)`,
      headlineHi: `${developmentalStage.stageNameHi} (${ak} आत्म-पाठ)`,
      bodyEn: `${developmentalStage.stageDescriptionEn} At the soul level, your Atmakaraka (${ak} in House ${akHouse}) presents this central curriculum: ${akLesson.lessonEn}`,
      bodyHi: `${developmentalStage.stageDescriptionHi} आत्मिक स्तर पर, आपके आत्मकारक ग्रह (${ak}) का मुख्य पाठ है: ${akLesson.lessonHi}`,
      bulletPointsEn: [
        `Current Milestone: ${developmentalStage.stageNameEn} (Age ${nativeAge})`,
        `Atmakaraka Curriculum: ${akLesson.lessonEn}`,
        `Evolutionary Horizon: ${developmentalStage.evolutionaryHorizonEn}`
      ],
      bulletPointsHi: [
        `वर्तमान कालखंड: ${developmentalStage.stageNameHi} (आयु ${nativeAge} वर्ष)`,
        `आत्मकारक साधना पाठ: ${akLesson.lessonHi}`,
        `भावी संभावना: ${developmentalStage.evolutionaryHorizonHi}`
      ],
      evidenceSummaryEn: `Calculated from Atmakaraka ${ak} in House ${akHouse} and native age (${nativeAge} years).`,
      evidenceSummaryHi: `आत्मकारक ${ak} (${akHouse}वें भाव) एवं जातक की वर्तमान आयु (${nativeAge} वर्ष) से आकलित।`
    },

    // Card 5: Dharma & Moksha
    card5_dharmaAndMoksha: {
      cardId: 'card5_dharmaAndMoksha',
      titleEn: '5. Dharma & Mokṣa',
      titleHi: '५. धर्म एवं मोक्ष समन्वय',
      sanskritTerm: 'धर्म-मोक्ष संतुलन (Dharma-Mokṣa)',
      headlineEn: dharmaMokshaRelationship.patternTitleEn,
      headlineHi: dharmaMokshaRelationship.patternTitleHi,
      bodyEn: dharmaMokshaRelationship.synthesisEn,
      bodyHi: dharmaMokshaRelationship.synthesisHi,
      bulletPointsEn: [
        `Vocation & Profession: ${careerExpression.vocationalChannelEn}`,
        `Material Prosperity: ${materialLifeExpression.wealthAndSpiritualHarmonyEn}`,
        `Detachment Clarity: ${vairagyaThemes.protectiveClarificationEn}`
      ],
      bulletPointsHi: [
        `आजीविका एवं कर्म: ${careerExpression.vocationalChannelHi}`,
        `समृद्धि संतुलन: ${materialLifeExpression.wealthAndSpiritualHarmonyHi}`,
        `वैराग्य विवेक: ${vairagyaThemes.protectiveClarificationHi}`
      ],
      evidenceSummaryEn: `Synthesizes 10th House (${h10Rasi}), 9th House (${h9Rasi}), and 12th House (${h12Rasi}).`,
      evidenceSummaryHi: `दशम भाव (${h10Rasi}), नवम भाव (${h9Rasi}) एवं द्वादश भाव (${h12Rasi}) का शास्त्रीय समन्वय।`
    },

    // Card 6: Challenges
    card6_spiritualChallenges: {
      cardId: 'card6_spiritualChallenges',
      titleEn: '6. Spiritual Challenges & Shadows',
      titleHi: '६. विकासात्मक चुनौतियाँ व सावधानियाँ',
      sanskritTerm: 'मार्ग के संशय (Sādhana Pratibandhaka)',
      headlineEn: challenges[0]?.challengeTitleEn || 'Maintaining Consistent Practice',
      headlineHi: challenges[0]?.challengeTitleHi || 'नियमित साधना का संतुलन',
      bodyEn: `Every genuine seeker encounters distinct developmental hurdles. In your chart, vigilance is helpful around ${challenges[0]?.challengeTitleEn.toLowerCase()} and maintaining grounded humility.`,
      bodyHi: `प्रत्येक साधक के मार्ग में कुछ विकासात्मक चुनौतियाँ आती हैं। आपकी कुंडली में विशेष रूप से "${challenges[0]?.challengeTitleHi}" के प्रति सजग रहना आवश्यक है।`,
      bulletPointsEn: challenges.map(c => `${c.challengeTitleEn}: ${c.growthOpportunityEn}`),
      bulletPointsHi: challenges.map(c => `${c.challengeTitleHi}: ${c.growthOpportunityHi}`),
      evidenceSummaryEn: `Identified from chart planetary configurations and protective classical remedies.`,
      evidenceSummaryHi: `कुंडली के ग्रह योगों एवं शास्त्रीय सावधानियों के आधार पर मूल्यांकित।`
    },

    // Card 7: Journey Over Time
    card7_spiritualJourneyOverTime: {
      cardId: 'card7_spiritualJourneyOverTime',
      titleEn: '7. Your Spiritual Journey Over Time',
      titleHi: '७. जीवन-कालक्रम में आध्यात्मिक यात्रा',
      sanskritTerm: 'काल-क्रम चेतना (Kāla-Krama)',
      headlineEn: `Current Chapter: ${spiritualLifeChapters.find(c => c.isCurrentChapter)?.chapterTitleEn} (Age ${nativeAge})`,
      headlineHi: `वर्तमान कालखंड: ${spiritualLifeChapters.find(c => c.isCurrentChapter)?.chapterTitleHi} (आयु ${nativeAge} वर्ष)`,
      bodyEn: `Spirituality is a developmental river flowing across the major seasons of your life. At Age ${nativeAge}, you are currently navigating Chapter ${spiritualLifeChapters.find(c => c.isCurrentChapter)?.chapterNumber}: ${spiritualLifeChapters.find(c => c.isCurrentChapter)?.themeEn}`,
      bodyHi: `आध्यात्मिकता जीवन के विभिन्न कालखंडों में बहने वाली चेतना की सरिता है। वर्तमान आयु (${nativeAge} वर्ष) में आप कालखंड ${spiritualLifeChapters.find(c => c.isCurrentChapter)?.chapterNumber} (${spiritualLifeChapters.find(c => c.isCurrentChapter)?.chapterTitleHi}) में हैं।`,
      bulletPointsEn: spiritualLifeChapters.map(c =>
        `${c.isCurrentChapter ? '▶ CURRENT: ' : ''}${c.chapterTitleEn} (${c.approxAgeRange}): ${c.themeEn}`
      ),
      bulletPointsHi: spiritualLifeChapters.map(c =>
        `${c.isCurrentChapter ? '▶ सक्रिय: ' : ''}${c.chapterTitleHi} (${c.approxAgeRange}): ${c.themeHi}`
      ),
      evidenceSummaryEn: `Mapped across native chronological age (${nativeAge} yrs) and Vimshottari Mahadasha timeline.`,
      evidenceSummaryHi: `जातक की वास्तविक आयु (${nativeAge} वर्ष) एवं विंशोत्तरी महादशा चक्र पर आधारित।`
    },

    // Card 8: What is Active Now
    card8_whatIsActiveNow: {
      cardId: 'card8_whatIsActiveNow',
      titleEn: '8. What is Spiritually Active Now',
      titleHi: '८. वर्तमान में क्या सक्रिय है?',
      sanskritTerm: 'वर्तमान स्थिति (Vartamāna Sthiti)',
      headlineEn: currentSpiritualState.currentThemeEn,
      headlineHi: currentSpiritualState.currentThemeHi,
      bodyEn: `${currentSpiritualState.dashaSpiritualRoleEn} ${currentSpiritualState.gocharTriggerSummaryEn}`,
      bodyHi: `${currentSpiritualState.dashaSpiritualRoleHi} ${currentSpiritualState.gocharTriggerSummaryHi}`,
      bulletPointsEn: [
        `Active Dasha Window: ${currentSpiritualState.windowStartDate} → ${currentSpiritualState.windowEndDate}`,
        `Current Dasha Resonance: ${currentSpiritualState.activeDashaPeriod}`,
        `Live Panchanga: ${spiritualPanchangaContext.tithiName} (${spiritualPanchangaContext.tithiPaksha}), ${spiritualPanchangaContext.nakshatraName} Nakshatra`,
        ...(activeObservances.length > 0 ? [`Active Observance / Parva: ${activeObservances.join(' | ')}`] : []),
        `Jupiter Transit: House ${jupiterTransitHouseLagna} from Lagna, House ${jupiterTransitHouseMoon} from Moon`,
        `Saturn Transit: House ${saturnTransitHouseLagna} from Lagna, House ${saturnTransitHouseMoon} from Moon`,
        `Actionable Focus: ${currentSpiritualState.recommendedFocusEn}`
      ],
      bulletPointsHi: [
        `सक्रिय काल-खंड: ${currentSpiritualState.windowStartDate} से ${currentSpiritualState.windowEndDate}`,
        `दशा संमिलन: ${currentSpiritualState.activeDashaPeriod}`,
        `पंचांग स्थिति: ${spiritualPanchangaContext.tithiName} (${spiritualPanchangaContext.tithiPaksha === 'Shukla Paksha' ? 'शुक्ल पक्ष' : 'कृष्ण पक्ष'}), ${spiritualPanchangaContext.nakshatraName} नक्षत्र`,
        ...(activeObservancesHi.length > 0 ? [`सक्रिय व्रत / पर्व: ${activeObservancesHi.join(' | ')}`] : []),
        `गुरु गोचर: लग्न से ${jupiterTransitHouseLagna}वां भाव, चंद्र से ${jupiterTransitHouseMoon}वां भाव`,
        `शनि गोचर: लग्न से ${saturnTransitHouseLagna}वां भाव, चंद्र से ${saturnTransitHouseMoon}वां भाव`,
        `सुझाव: ${currentSpiritualState.recommendedFocusHi}`
      ],
      evidenceSummaryEn: `Anchored to focus date (${focusDateStr}), ${spiritualPanchangaContext.tithiName}, active ${dashaAtFocus.maha}–${dashaAtFocus.antar} Dasha, and live transits.`,
      evidenceSummaryHi: `फोकस दिनांक (${focusDateStr}), ${spiritualPanchangaContext.tithiName}, सक्रिय ${dashaAtFocus.maha}–${dashaAtFocus.antar} दशा एवं तात्कालिक गोचर पर आधारित।`
    }
  };

  // --------------------------------------------------------------------------
  // ASSEMBLE FORENSIC ACHARYA PRESENTATION
  // --------------------------------------------------------------------------
  const acharya: AcharyaSpiritualityPresentation = {
    d1BhavaFactors: [
      {
        bhavaNum: 9,
        sign: h9Rasi,
        lord: h9Lord,
        occupants: h9Occupants,
        aspects: ['Jupiter', 'Sun'],
        shastricAssessment: `9th Bhava (${h9Rasi}) ruled by ${h9Lord} (placed in House ${h9LordHouse}). Foundation of Dharma, Guru blessings, moral conviction, and higher philosophical knowledge.`
      },
      {
        bhavaNum: 12,
        sign: h12Rasi,
        lord: h12Lord,
        occupants: h12Occupants,
        aspects: ['Saturn'],
        shastricAssessment: `12th Bhava (${h12Rasi}) ruled by ${h12Lord} (placed in House ${h12LordHouse}). Moksha sthana presiding over mental release, silent meditation, and inner transcendence.`
      },
      {
        bhavaNum: 5,
        sign: h5Rasi,
        lord: h5Lord,
        occupants: h5Occupants,
        aspects: ['Jupiter'],
        shastricAssessment: `5th Bhava (${h5Rasi}) Purva Punya sthana ruled by ${h5Lord} (placed in House ${h5LordHouse}). Governs mantra siddhi, intellectual acuity (Dhi), and innate spiritual merit.`
      },
      {
        bhavaNum: 8,
        sign: h8Rasi,
        lord: h8Lord,
        occupants: h8Occupants,
        aspects: [],
        shastricAssessment: `8th Bhava (${h8Rasi}) Randhra sthana ruled by ${h8Lord} (placed in House ${h8LordHouse}). Governs deep esoteric research, transformational crises, and alchemical maturation.`
      }
    ],
    karakasAnalysis: {
      ak,
      akHouse,
      karakamshaSign: karakamsha.signEn,
      bk,
      bkHouse,
      ishtaDevataFactor: `12th from Karakamsha in D9 (${ishtaDevata.twelfthSignD9}) signifies spiritual alignment with ${ishtaDevata.deityEn}.`
    },
    d20VimsamshaFactors: [
      {
        varga: 'D20',
        factorName: 'Devaguru Jupiter Placement',
        factorPlacement: d20Jupiter ? `${d20Jupiter.sign} (D20)` : 'Benefic Alignment',
        classicalSignificance: 'Establishes authentic devotion and receptivity to sacred tradition.',
        evidenceScore: 35
      },
      {
        varga: 'D20',
        factorName: 'Moksha Karaka Ketu Placement',
        factorPlacement: d20Ketu ? `${d20Ketu.sign} (D20)` : 'Karmic Detachment Point',
        classicalSignificance: 'Deepens inward contemplation and release of material obsession.',
        evidenceScore: 30
      },
      {
        varga: 'D9',
        factorName: `Atmakaraka ${ak} in Navamsha`,
        factorPlacement: karakamsha.signEn,
        classicalSignificance: 'Karakamsha reveals the foundational soul orientation per Maharishi Jaimini.',
        evidenceScore: 35
      }
    ],
    nakshatraSpiritualInfluences: (kundali.planets || []).slice(0, 5).map(p => ({
      planet: p.name as PlanetName,
      nakshatra: p.nakshatra || 'Ashwini',
      spiritualImprint: `Channels ${p.name} energy into moral discipline and philosophical reflection.`
    })),
    dashaHierarchyEvidence: {
      md: dashaAtFocus.maha,
      ad: dashaAtFocus.antar,
      pd: dashaAtFocus.pratyantar,
      spiritualActivationRating: isDashaSpiritual ? 'HIGH (Direct Spiritual Stimulus)' : 'MODERATE (Dharmic Grounding)'
    },
    gocharEvidence: {
      jupiterTransitHouse: jupiterTransitHouseLagna,
      saturnTransitHouse: saturnTransitHouseLagna,
      ketuTransitHouse: ketuTransitHouseLagna,
      impact: `Jupiter in House ${jupiterTransitHouseLagna} expands dharmic awareness; Saturn in House ${saturnTransitHouseLagna} enforces patience; Ketu in House ${ketuTransitHouseLagna} facilitates mental detachment.`
    },
    panchangaProvenance: spiritualPanchangaContext,
    contradictionAnalysis: contradictions,
    negativeEvidenceNotes: [
      'Lack of severe malefic combustion on 9th lord preserves ethical clarity.',
      'Absence of fanatic distortion on 12th house prevents destructive escapism or dogmatic delusion.'
    ],
    shastricCitations: [
      {
        shastra: 'Brihat Parashara Hora Shastra',
        slokaRef: 'Ch. 24, Sloka 31',
        excerpt: 'धर्मे गुरुयुते वापि धर्मेशे केन्द्रकोणगे। जातकः सत्यवादी च धर्मकर्मपरायणः॥',
        application: 'Affirms that Jupiter connected with 9th house or 9th lord in Kendra/Trikona creates a truthful native dedicated to righteous conduct.'
      },
      {
        shastra: 'Jaimini Upadesha Sutras',
        slokaRef: 'Adhyaya 1, Pada 2, Sutra 69',
        excerpt: 'तत्र केतौ कैवल्यम्।',
        application: 'Ketu in the 12th from Karakamsha or 12th house indicates the path of inward liberation and detachment.'
      },
      {
        shastra: 'Srimad Bhagavad Gita',
        slokaRef: 'Ch. 3, Sloka 19',
        excerpt: 'तस्मादसक्तः सततं कार्यं कर्म समाचर। असक्तो ह्याचरन्कर्म परमाप्नोति पूरुषः॥',
        application: 'The classical foundation for Karma Yoga: perform necessary duties without attachment to reach supreme peace.'
      }
    ]
  };

  return {
    evidenceObject,
    seeker,
    acharya,
    calculationVersion: 'SIME-V1.0-PARASHARI-JAIMINI',
    calculatedAt: new Date().toISOString()
  };
}
